// src/encounter/patient-summary-rail.tsx
// LEFT column of the 3-column pre-encounter shell (Flutter encounter_screen.dart:1494, 328px
// fixed left rail). Header lives INSIDE the rail (not a top banner). Composes DS
// PatientHeader (the Figma-recovered identity + 4-card block, DS 1.50 — PROD-1588 round 3)
// + InternationalPatientSummary; owns the per-section interactivity:
//   • Flags        — add + soft-delete (list-replace POST)            (PROD-1255)
//   • Medications  — add (coded search) + soft-delete                 (PROD-1257)
//   • Allergies    — add + edit + soft-delete                         (PROD-1265)
//   • Problems     — active/resolved add + edit (incl active↔resolved) + soft-delete (PROD-1265)
//   • Procedures   — add (coded CPT search) + edit + soft-delete       (PROD-1266)
// Edit/delete need the FULL chart record (recorder/asserter/onset/…) which the lossy
// GetPatientSummary projection omits — so the rail also reads the chart-detail lists
// (GetPatientAllergyDetails / GetConditionStatusDetails) and looks records up by identifier.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  InternationalPatientSummary,
  Button,
  Skeleton,
  SessionStatusControl,
  EncounterSummaryBand,
  type EncounterSummaryCard,
  type PatientHeaderData,
  type PatientHeaderDemographics,
  type IpsSection,
  type IpsEntry,
  type CodeSearchResult,
} from '@scripsteam/scrips-signal-ds';
import { railSectionCard, type RailSurfaceRender } from './pre-encounter-parts';
// LEGACY rail (left-rail target 4 Nov 2026 §5). `ChartColumnV2` and the DS `PreEncounterSummaryRail` are drawn ONLY for hosts where
// `railV3` is OFF; this is their one import boundary. They get no new features: the rail is `RailV3Column` (Antonin's catalogue parts).
import { PreEncounterSummaryRail } from '@scripsteam/scrips-signal-ds';
import { ChartColumnV2 } from './chart-column-v2';
import type { ChartColumnSection, ChartRowExtras, RailRowHandlers } from './lib/chart-rail-types';
// The rail: Antonin's catalogue composition (rail-v3/rail-v3-column.tsx).
import { RailV3Column } from './rail-v3/rail-v3-column';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { DeviceTrendsSlot } from './device-trends/device-trends-slot';
import { isoDayMonthYear, isoMonthYear, isoYear, type ChartSortFacts } from './lib/chart-column-model';
import { useStableChartOrder, type ChartSectionSortInput } from './lib/use-stable-chart-order';
import { useDesignV2 } from './lib/design-v2';
import patientUserPicUrl from '@/assets/images/patient-userpic.png';
import { RailAddPopoverBody } from './rail-add-popover';
import { EncounterStatusControl } from './encounter-status-control';
import { visitGlyph } from '@/patients/lib/overview-headlines';
import { showServerError } from '@/shared/lib/toast-helpers';
import type { ContactVM } from './lib/to-patient-contacts';
import { FlagAddDialog } from './flag-add-dialog';
import { useFlagCategories } from './services/use-flag-categories';
import { flagPersonaFor, offerableFlagGroups } from './lib/flag-offer';
import { useAuthStore } from '@/auth/auth-store';
import { useSaveFlags } from './services/use-save-flags';
import { buildCreateFlagBody } from './lib/to-flag-request';
import { MedicationAddDialog, type MedicationEditPrefill } from './medication-add-dialog';
import { IpsMedStopDialog } from './ips-med-stop-dialog';
import { useMedicationSearchTiered } from './services/use-medication-search-tiered';
import { useSaveMedication, getMedicationDetails } from './services/use-save-medication';
import { useDrugInteractions } from './services/use-drug-interactions';
import { buildDrugInteractionInput } from './lib/to-drug-interaction-input';
import { buildCreateMedicationBody, toMedicationEditPrefill, type MedicationFormInput } from './lib/to-medication-request';
import { IpsAllergyDialog } from './ips-allergy-dialog';
import { IpsConditionDialog } from './ips-condition-dialog';
import { IpsNoteDialog } from './ips-note-dialog';
import { IpsProcedureDialog } from './ips-procedure-dialog';
import { IpsImmunizationDialog } from './ips-immunization-dialog';
import { IpsFamilyHistoryDialog } from './ips-family-history-dialog';
import { IpsSocialHistoryDialog } from './ips-social-history-dialog';
import { IpsOccupationDialog } from './ips-occupation-dialog';
import { IpsEducationDialog } from './ips-education-dialog';
import { useSaveAllergy } from './services/use-save-allergy';
import { useSaveCondition } from './services/use-save-condition';
import { useSaveProcedure } from './services/use-save-procedure';
import { useSaveImmunization } from './services/use-save-immunization';
import { useSaveFamilyHistory } from './services/use-save-family-history';
import { useSaveSocialHistory } from './services/use-save-social-history';
import { useSaveOccupation } from './services/use-save-occupation';
import { useSaveEducation } from './services/use-save-education';
import { useEducationLevels } from './services/use-education-levels';
import {
  buildAllergyRequest,
  medicationToAllergen,
  type AllergenKind,
  type AllergyFormValue,
} from './lib/to-allergy-request';
import { useMedicationValueSetSearch } from './services/use-medication-valueset-search';
import {
  buildConditionAppendNoteRequest,
  buildConditionEnteredInErrorRequest,
  buildConditionRequest,
  type ConditionFormValue,
} from './lib/to-condition-request';
import {
  buildProcedureRequest,
  type ProcedureFormValue,
} from './lib/to-procedure-request';
import {
  buildImmunizationRequest,
  normalizeVaccineCoding,
  type ImmunizationFormValue,
} from './lib/to-immunization-request';
import {
  buildFamilyHistoryRequest,
  type FamilyHistoryFormValue,
  type FamilyHistoryRelationship,
} from './lib/to-family-history-request';
import {
  buildSocialHistoryRequest,
  type SocialHistoryFormValue,
} from './lib/to-social-history-request';
import { buildOccupationRequest, type OccupationFormValue } from './lib/to-occupation-request';
import { buildEducationRequest, type EducationFormValue } from './lib/to-education-request';
import {
  allergyOptionsToCodeSearchResults,
  flattenDiagnosisRow,
  procedureOptionsToCodeSearchResults,
  socialTypeOptionsToCodeSearchResults,
  toCodeSearchResults,
  vaccineOptionsToCodeSearchResults,
} from './lib/to-code-search-result';
import { problemToCodeSearchResult, toPatientProblems } from './lib/patient-problems';
import { useAllergyCodeSearch } from '@/patients/services/use-allergy-code-search';
import { useAllergenSeverities } from '@/patients/services/use-allergen-severities';
import { useAllergenReactions } from '@/patients/services/use-allergen-reactions';
import { usePatientAllergies } from '@/patients/services/use-patient-allergies';
import { useDiagnosisCodeSearch } from '@/patients/services/use-diagnosis-code-search';
import { usePatientConditions } from '@/patients/services/use-patient-conditions';
import { useProcedureCodeSearch } from '@/patients/services/use-procedure-code-search';
import { normalizeProcedureCode } from '@/patients/lib/procedures';
import { usePatientProcedures } from '@/patients/services/use-patient-procedures';
import { useVaccineCodeSearch } from '@/shared/services/use-vaccine-code-search';
import { usePatientImmunizations } from '@/patients/services/use-patient-immunizations';
import { usePatientFamilyHistory } from '@/patients/services/use-patient-history';
import { useRelationships } from '@/services/common/use-relationships';
import { useSocialHistoryCodeSearch } from '@/patients/services/use-social-history-code-search';
import { useSocialHabitQtyList } from '@/patients/services/use-social-habit-qty-list';
import { usePatientSocialHistory } from '@/patients/services/use-patient-history';
import { CONDITION_STATUS } from '@/types/patient-chart-api';
import type {
  AllergyCodeOption,
  CodeDisplayName,
  ConditionCode,
  ImmunizationCoding,
  PatientAllergy as ChartAllergy,
  PatientCondition as ChartCondition,
  PatientProcedure as ChartProcedure,
  PatientImmunization as ChartImmunization,
  PatientFamilyHistory as ChartFamilyHistory,
  PatientSocialHistory as ChartSocialHistory,
  SocialHistoryCodeRow,
} from '@/types/patient-chart-api';
import type { FlagCategory } from '@/types/flags-api';
import type { PatientFlag } from '@/types/patient-summary-api';
import type { PatientOccupation, PatientEducation } from '@/types/patient-detail-api';
import type { MedicationPlanIntent } from './lib/medication-record-to-seed';
import { useRole } from '@/auth/use-role';
import {
  railV3BindActions,
  railV3MedicationActions,
  railV3ProblemActions,
  railV3ResolvedActions,
  type RailV3ActionContext,
} from './rail-v3/rail-v3-actions';
import {
  LANDING_ABSENT,
  LANDING_READY,
  NO_NOTE_REASON,
  allergyNoteLine,
  landingBlocked,
  medicationNoteLine,
  problemNoteLine,
  railNotePiece,
  type RailNotePieceHost,
} from './lib/rail-note-piece';
import {
  RAIL_NOTE_SECTION_KIND,
  soleTarget,
  targetsFor,
  type RailNoteRowKind,
  type RailNoteTargetId,
  type RailNoteTargets,
  type RailTargetLanding,
} from './rail-v3/rail-note-targets';

/** One labeled line within a left-rail social card (Occupation / Education). `label` is the value,
 *  `sublabel` the category eyebrow (e.g. "Employment", "Job info", "Education level"). A card can
 *  carry several rows — Flutter renders the combined employment row AND a "Job info" row, etc. */
export interface RailSocialEntry {
  label: string;
  sublabel?: string;
}

export interface PatientSummaryRailProps {
  patient: PatientHeaderData;
  demographics?: PatientHeaderDemographics;
  ipsSections: IpsSection[];
  /** Occupation / Education card rows (each a labeled line). Empty/absent → the card is omitted
   *  (read-only). When interactive (patientId + currentUserId), the cards always render with an
   *  Add/Edit affordance, even when empty (PROD-1272). */
  occupation?: RailSocialEntry[];
  education?: RailSocialEntry[];
  /** Raw Occupation/Education entities (PROD-1272) — used to pre-fill the edit dialog + round-trip
   *  the entity `id` on the PUT upsert. The display rows above are lossy, so the edit path needs
   *  these. */
  occupationEntity?: PatientOccupation | null;
  educationEntity?: PatientEducation | null;
  /** Raw patient flags (PROD-1989) — the flags write path is a WHOLE-LIST REPLACE and the body must
   *  carry each existing flag's numeric `flagResponse.code`. `ipsSections` cannot supply it: the DS
   *  `IpsEntry` is presentational, so `to-ips-sections` uses the code to dedupe and then drops it.
   *  Same reason `occupationEntity`/`educationEntity` exist. Absent → flags render display-only. */
  flagRecords?: PatientFlag[] | null;
  /** When set, the Flags + Medications + Allergies + Problems sections become interactive. */
  patientId?: string;
  /** Provider id — CreateFlag createdBy, medication-search ProviderId, and the allergy
   *  `recorder` / condition `asserter` stamped on CREATE. */
  currentUserId?: string;
  /** Current user's display name — stamped on the condition CREATE performer sentinel. */
  currentUserName?: string | null;
  /** Encounter id — REQUIRED for medication create (CreateMedication.encounter). When absent,
   *  the Medications section stays display-only (PROD-1257). */
  encounterId?: string;
  /** Practitioner id (CreateMedication administration.performer). Falls back to currentUserId. */
  performerId?: string;
  /** PROD-2669: offer "Stop" on a medication row. Physicians only (`useRole().mayStopOrSuspend`,
   *  the Scrips.Common allowlist {3, 8, 18}); a nurse reconciles with Edit but does not stop.
   *  Defaults to FALSE so a host that forgets to pass it offers no stop. */
  canStopMedication?: boolean;
  /** `medActions` (Samer's Loom, 3 Nov): in a STARTED encounter, for a user who may order medications, a medication row also
   *  offers Continue · Refill · Change, each handed to the A&P Plan as today's medication order (the page owns the seed). Absent:
   *  the row is as before (Edit, and Stop for a physician). */
  onMedicationToPlan?: (identifier: string, intent: MedicationPlanIntent) => void;
  /** Live appointment ("session") status (e.g. "Arrived"/"Booked") → status badge top-of-card
   *  (Flutter `patient_info_widget` status). Read-only here; the status-change menu is deferred.
   *  Sourced from tap-time nav (like the Reschedule slot/status, PROD-1246/1256) — so on a
   *  reload/deep-link it's absent and the badge hides (the rest of the card still enriches from the
   *  patient fetch). Surviving reload needs a standalone appointment-status fetch (encounter status
   *  ≠ appointment status), a follow-up — NOT the encounter status, which uses a different vocab. */
  appointmentStatus?: string;
  /** Appointment id (encounter route param). When present, the header renders an INTERACTIVE
   *  status control so the clinician can change the appointment status from the encounter
   *  (Flutter patient_info_widget parity — the previously-deferred status-change menu). When
   *  absent (deep-link/reload without nav), the read-only badge from `appointmentStatus` shows. */
  appointmentId?: string;
  /** Patient importance / VIP marker (Flutter IndicatorTagWidget). Accepted for call-site
   *  stability but NOT currently rendered: the DS 1.50 PatientHeader block (which replaced
   *  PatientSummaryHeader here, PROD-1588 round 3) has no importance prop/slot yet — flagged
   *  as a DS follow-up; re-wire when the block grows one. */
  importance?: { code?: string | null; display?: string | null; definition?: string | null } | null;
  /** Relative / emergency contacts → the contacts block under the card (Flutter ContactWidget). */
  contacts?: ContactVM[];
  /**
   * Presentational arrangement (Flutter `patient_summary_widget` orientation):
   *   • 'rail'   (default, PRE-encounter) — a 344px vertical aside (E2 spec column): the COMPACT
   *              identity card on top (no info-card cluster — round-4 reject #1), single-column
   *              IPS segment list below at 312w/inset 16 (Flutter portrait `ListView`).
   *   • 'banner' (IN-encounter) — the shell frame's dense summary band (app-local
   *              `EncounterSummaryBand`, built to EncounterScreen.dc.html): collapsed, a 288px
   *              identity card beside ONE cluster card split into two halves carrying FIVE
   *              sections; expanded, the identity card goes FULL WIDTH with the interactive
   *              2-column IPS masonry below it, behind a "Full summary" disclosure (Flutter
   *              `isPatientInfoViewExpanded`, default collapsed).
   *   • 'chart'  (IN-encounter, frame v2 LEADING column, PROD-2743) — the approved design's chart
   *              column (`chart-column-v2.tsx`): the DS `ClinicalTermRow` family in cards with
   *              depth, wayfinding-ink titles, Allergies first, resolved problems in a collapsed
   *              drawer, a person-first patient card. ON-only: with the `frameV2` switch OFF it
   *              renders exactly as 'rail' (the page never builds this layout with it off).
   * The interactivity (add/edit/delete dialogs + chart-detail reads) is identical in all three —
   * only the outer layout differs.
   */
  layout?: 'rail' | 'banner' | 'chart';
  /** 'chart' only: the visit has started. The column then draws no visit card and no MRN row (Antonin, 1 Nov; the MRN
   *  is the page header's). Read by no other layout. */
  started?: boolean;
  /** 'chart' only: draw the visit status as the design's read-only DS `StatusChip` (D-49 tones: the
   *  live CheckedIn state the one solid chip, in the field's own words, PROD-3045 N5) instead of the
   *  status control. The STARTED encounter passes it; a host that omits it keeps the control, which
   *  is where arrival is recorded before a visit. */
  statusChip?: boolean;
  /** 'chart' only: draw NO status at all in the person card (the long page's one status is the header's chip). */
  hideStatus?: boolean;
  /** Pre-visit, rail v3: the page header carries the identity and the status control (`RailStatusControl`), so the person
   *  card draws neither (Andrew, 3 Nov: "Duplication in headers in the pre encounter screen"). */
  identityInHeader?: boolean;
  /** The summary read (or the patient lookup it waits on) is in flight → a loader, never the
   *  sections' "No … recorded" empty states (R6). */
  loading?: boolean;
  /**
   * PROD-2804 / R6: the aggregate summary read FAILED. Every section here is drawn from that one
   * read, so a failure must not render as an empty record: the sections are replaced by
   * "couldn't be loaded" and a Try again (`onRetry`). "No … recorded" only after a successful read.
   */
  loadError?: boolean;
  /** Retry for `loadError` — the host refetches the summary. */
  onRetry?: () => void;
  /**
   * PROD-2804 / R6: ids of the sections whose field the (successful) summary read did NOT deliver
   * as a list (`unavailableSummarySections`). Such a section says "… unavailable", never
   * "No … recorded" — an omitted field is not an empty record.
   */
  unavailableSections?: readonly string[];
  /**
   * The started long page only: an Active Problems row's "Add to note" adds that problem as a diagnosis in the
   * encounter's Assessment (the page's own add path) instead of opening the "+ Note" dialog. Absent (pre-encounter,
   * `?ef=-frameV2`, after the provider sign) → the row keeps "+ Note". A problem with no code offers no action.
   */
  onAddProblemToNote?: (dx: CodeSearchResult) => void;
  /**
   * Rail v3, the nurse (left-rail target §3): "Reconcile medications" opens the nurse reconciliation card (#916) on this page.
   * Absent → no Reconcile action (a host without the card offers none).
   */
  onReconcileMedications?: () => void;
  /**
   * Rail v3, the started visit (left-rail target §4): the current encounter note, for "Add to note". Absent → the action is shown
   * disabled with the reason (`NO_NOTE_REASON`); it never writes anywhere else.
   */
  notePieces?: RailNotePieceHost;
  /**
   * CONTROLLED banner disclosure (Flutter `isPatientInfoViewExpanded`). Supply it and the rail
   * stops owning the toggle: it renders the state you pass and hides its own "Full summary"
   * button, because the SHELL's chevron owns both halves of the state.
   *
   * Flutter drives band HEIGHT and content MODE from that one flag (encounter_screen.dart:
   * 1112-1136 sets the band to a fixed 180px / 70% of the frame, and :1449-1454 flips
   * `ClinicalInfoViewType.view` -> `.editable`). Two separate controls — a shell chevron for
   * height and a rail button for content — is one control too many for the same idea.
   * Omit for the uncontrolled behaviour (the rail keeps its own button).
   */
  expanded?: boolean;
}

/**
 * Join a demographics run the way Flutter does — ONE string with " • " between the present parts
 * (`patient_info_widget.dart:257`), so the bullets carry the run's own colour and the line wraps as
 * plain text. The previous `DemoLine` element rendered per-part nowrap spans with hint-tinted
 * bullets, which is not what the source does.
 */
function joinRun(parts: (string | false | undefined)[], sep = ' • '): string | undefined {
  const shown = parts.filter(Boolean) as string[];
  return shown.length > 0 ? shown.join(sep) : undefined;
}

const SEX_DISPLAY: Record<string, string> = { M: 'Male', F: 'Female', X: 'Other' };



// `--color-text-tertiary` does NOT exist in the DS token set — an earlier value resolved to nothing
// and the eyebrow inherited its parent colour. `--color-text-hint` is the real muted rung.
const EYEBROW_TYPE =
  'text-size-xs font-semibold tracking-eyebrow uppercase text-text-hint';
const EYEBROW = `mt-0 mx-0 mb-2 ${EYEBROW_TYPE}`;
// Now that the IPS sections render as accent-striped cards (sectionStyle="card"), these two sit
// among them on the rail — PreEncounter.dc.html draws Occupation/Education with the SAME anatomy as
// every other section. Match the DS card's neutral stripe + elevation so the rail reads as one card
// family rather than two. (The stripe is the DS `neutral` accent colour.)
const SOCIAL_CARD =
  'bg-surface-card border border-solid border-border-default ' +
  'border-l-3 border-l-brand-primary rounded-lg ' +
  'p-4 shadow-2';

const CARD_TITLE = `m-0 ${EYEBROW_TYPE}`;
const CARD_EMPTY = 'm-0 text-size-sm text-text-secondary italic';

/** One social card (Occupation / Education) with a title, an optional Add/Edit affordance, and one
 *  or more labeled rows. Mirrors Flutter's occupation_info_widget / education_info_widget: a header
 *  with an add (empty) / edit (populated) action, then stacked eyebrow+value rows (e.g. the combined
 *  employment row plus a "Job info" row) or a "No … added" placeholder. When `onAddEdit` is omitted
 *  the card is read-only (no affordance). */
function SocialSection({
  title,
  rows,
  onAddEdit,
  busy,
}: {
  title: string;
  rows: RailSocialEntry[];
  onAddEdit?: () => void;
  busy?: boolean;
}) {
  const hasData = rows.length > 0;
  return (
    <div className={SOCIAL_CARD}>
      <div className="flex items-center justify-between mb-2">
        <p className={CARD_TITLE}>{title}</p>
        {onAddEdit && (
          <Button
            variant="tertiary"
            size="sm"
            onClick={onAddEdit}
            disabled={busy}
            aria-label={`${hasData ? 'Edit' : 'Add'} ${title.toLowerCase()}`}
          >
            {hasData ? 'Edit' : 'Add'}
          </Button>
        )}
      </div>
      {hasData ? (
        rows.map((row, i) => (
          <div key={`${row.sublabel ?? ''}-${i}`} className={i === 0 ? 'mt-0' : 'mt-3'}>
            {row.sublabel && <p className={EYEBROW}>{row.sublabel}</p>}
            {/* Match the DS IPS entry primary (InternationalPatientSummary: 14px, normal
                weight, text-primary) so Occupation/Education rows are the same size as the other
                IPS sections — they previously rendered larger (font-size-md + 600). */}
            <p className="m-0 text-size-sm text-text-primary">
              {row.label}
            </p>
          </div>
        ))
      ) : (
        <p className={CARD_EMPTY}>No {title.toLowerCase()} added</p>
      )}
    </div>
  );
}

/** PROD-2804 / R6: the copy for a section whose summary field was NOT delivered (absent / null).
 *  Says the information is unavailable — never "No … recorded", which only a successful empty
 *  read may say, and never "No known allergies", which only a recorded assertion may say. */
const RAIL_UNAVAILABLE_LABEL: Record<string, string> = {
  flags: 'Flag information unavailable',
  allergies: 'Allergy information unavailable',
  medications: 'Medication information unavailable',
  'active-problems': 'Active problem information unavailable',
  'resolved-problems': 'Resolved problem information unavailable',
  procedures: 'Surgical history unavailable',
  vaccinations: 'Vaccination history unavailable',
  'family-history': 'Family history unavailable',
  'social-history': 'Social history unavailable',
};

/** The reason surface for "Entered in error" (left-rail target §3; a required reason, clinical ruling 1 Nov). */
const ENTERED_IN_ERROR_WORDS = {
  title: 'Entered in error',
  field: 'Reason',
  commit: 'Mark entered in error',
  placeholder: 'Why this entry is in error',
} as const;

/** Rail v3: a section's sort facts by record identifier (`sortSection`). */
function factsById<T extends { identifier?: string | null }>(
  rows: readonly T[] | null | undefined,
  pick: (row: T) => ChartSortFacts,
): ReadonlyMap<string, ChartSortFacts> {
  const map = new Map<string, ChartSortFacts>();
  for (const row of rows ?? []) if (row.identifier) map.set(row.identifier, pick(row));
  return map;
}

/** Defensive guard for a synthetic non-interactive "+N more" trailer row (id `${sectionId}-more`):
 *  it must never get an edit/delete affordance. to-ips-sections no longer caps/folds (PROD-1271 —
 *  Flutter lists all), so no trailer is produced today; kept as a cheap guard in case progressive
 *  disclosure is ever reintroduced. */
function isMoreTrailer(sectionId: string, entry: IpsEntry): boolean {
  return entry.id === `${sectionId}-more`;
}

/** One entry out of the summary sections, by (section, id) - the row data an anchored row
 *  surface needs when it is not backed by a chart record (Medications' Stop confirm only needs
 *  the display name). */
function findIpsEntryIn(sections: IpsSection[], sectionId: string, entryId: string): IpsEntry | undefined {
  return sections.find((s) => s.id === sectionId)?.entries.find((e) => e.id === entryId);
}

/** Dismisses the anchored surface it is rendered in, from an EFFECT.
 *
 *  Needed by exactly one flow: Medications Edit fetches its prefill after the popover has already
 *  opened, so a FAILED fetch would otherwise leave a permanent "Loading..." popover with no form
 *  and no way out but Escape. The pre-existing contract for that failure is "no dialog"
 *  (`patient-summary-rail.medications.test.tsx` — "Edit fetch failure leaves the dialog closed
 *  (no half-prefilled clinical form)"), and the error itself is already reported to the user by
 *  `showServerError`. A render function cannot call `close()` inline — that would be a side
 *  effect during render — so it renders this instead. */
function DismissOnMount({ close }: { close: () => void }) {
  useEffect(() => {
    close();
  }, [close]);
  return null;
}

/** The appointment status as the pre-visit draws it: interactive when the appointment id is known, else read-only. The status
 *  as the schedule draws it (Antonin, 16 Oct: "'checked in' is not consistent with the iconography and colour"): the shared
 *  status glyph (`visitGlyph`, the Calendar / Today / Overview mapping) before a plain label; the DS control's own pill is
 *  restyled by the bridge on `data-glyph` (its rail status block). The rail's person card mounts it, or the pre-visit page header
 *  when that carries the identity (`identityInHeader`). */
export function RailStatusControl({ appointmentStatus, appointmentId }: { appointmentStatus?: string; appointmentId?: string }) {
  if (!appointmentStatus) return null;
  return (
    <span data-slot="rail-status-glyph" data-glyph={visitGlyph(appointmentStatus)} className="contents">
      {appointmentId ? (
        <EncounterStatusControl appointmentId={appointmentId} status={appointmentStatus} />
      ) : (
        // `sm` is the person-card pill (the DS graduation of the old `.pa-person-status` override).
        <SessionStatusControl size="sm" value={appointmentStatus} readOnly aria-label={appointmentStatus} />
      )}
    </span>
  );
}

export function PatientSummaryRail({
  patient,
  demographics,
  ipsSections,
  flagRecords,
  occupation,
  education,
  occupationEntity,
  educationEntity,
  patientId,
  currentUserId,
  currentUserName,
  encounterId,
  performerId,
  canStopMedication = false,
  onMedicationToPlan,
  appointmentStatus,
  appointmentId,
  contacts,
  importance,
  layout = 'rail',
  started,
  statusChip = false,
  hideStatus = false,
  identityInHeader = false,
  loading,
  loadError = false,
  onRetry,
  unavailableSections,
  onAddProblemToNote,
  onReconcileMedications,
  notePieces,
  expanded,
}: PatientSummaryRailProps) {
  const isUnavailable = (sectionId: string) => !!unavailableSections?.includes(sectionId);
  /** The empty copy for a section: "unavailable" when its field was not delivered, else the
   *  record-describing empty label. */
  const emptyCopy = (sectionId: string, recordedEmpty: string) =>
    isUnavailable(sectionId) ? (RAIL_UNAVAILABLE_LABEL[sectionId] ?? 'Information unavailable') : recordedEmpty;
  // Antonin (1 Nov): a left-rail section with 0 items is folded to its title line ("Allergies · 0" and its `+`). The DS
  // card prints `emptyLabel` as given, so '' leaves an empty span the bridge hides; an unread list still says "unavailable".
  const foldedEmpty = (sectionId: string) => (isUnavailable(sectionId) ? emptyCopy(sectionId, '') : '');
  const banner = layout === 'banner';
  // Design sheet v1.1 (switch ON only): category tones on the rail cards; see `railSectionCard`.
  const designV2 = useDesignV2();
  // The chart column (the design's PatientSummaryColumn) on every 'chart' host, switch ON or OFF: the approved design's
  // look never depends on a switch (owner ruling, 21 Oct). The switch keeps only its own effects elsewhere in this file.
  const chartColumn = layout === 'chart';
  // `?x=rail-v3`: the left rail restyled to the designer's spec (rail-v3/). Read once per mount; OFF renders as before.
  const [railV3] = useState(() => isEncounterFlagOn('railV3'));
  // Rail v3 (left-rail target §3): the per-section action tables, on the pre-visit and the started rail (never the banner).
  const railV3Rows = railV3 && layout !== 'banner';
  const railV3Stage: 'pre-visit' | 'started' = layout === 'chart' && started ? 'started' : 'pre-visit';
  // The persona from the existing server-backed helper (no new role check).
  const { clinicalRole } = useRole();
  const [visitNoteV2] = useState(() => isEncounterFlagOn('visitNoteV2'));
  // In-encounter banner disclosure (Flutter isPatientInfoViewExpanded, default collapsed):
  // collapsed = the frame-true dense header band; expanded reveals the full interactive IPS.
  // CONTROLLED when the host passes `expanded` (the shell chevron owns it — see the prop docs);
  // otherwise owned here, which keeps every existing consumer and story unchanged.
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const controlled = expanded !== undefined;
  const bannerExpanded = controlled ? expanded : uncontrolledExpanded;
  const setBannerExpanded = setUncontrolledExpanded;
  const interactive = !!patientId;
  // Allergy/condition CREATE stamps recorder/asserter = currentUserId, so their write
  // affordances require it (edit/delete preserve the original, but the section is gated as a
  // whole so a clinician never sees an Add that would silently no-op — Codex MAJ). Display-only
  // when absent. Flags keep the patientId-only gate (buildCreateFlagBody tolerates no user).
  const clinicalWriteEnabled = !!patientId && !!currentUserId;
  // Medication create needs an encounter Guid (CreateMedication.encounter) — only interactive
  // once the encounter exists. The performer is the practitioner id (falls back to the user id).
  const medsInteractive = !!patientId && !!encounterId;
  const medPerformerId = performerId ?? currentUserId;
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  // The pre-encounter rail opens the flag picker as a popover ANCHORED to the section's `+`
  // (the published app's behaviour); the in-encounter BANNER still routes through the DS
  // organism and its centred `Modal`. Two independent open flags, deliberately: one shared
  // flag would raise both surfaces at once in the layout that has both wired.
  const [flagPopoverOpen, setFlagPopoverOpen] = useState(false);
  const categories = useFlagCategories(flagDialogOpen || flagPopoverOpen);
  // Only the flag types the signed-in persona may add (encounter owner ruling, row by row, 1 Nov
  // 2026). The persona comes from the app's role source; an unrecognised user is offered nothing,
  // and the picker then shows its existing "No flags available." line. See `lib/flag-offer.ts`.
  const flagPersona = flagPersonaFor(useAuthStore((s) => s.user));
  const offeredFlagGroups = useMemo(
    () => offerableFlagGroups(categories.data, flagPersona),
    [categories.data, flagPersona],
  );
  const saveFlags = useSaveFlags(patientId);

  // ── Medications (PROD-1257 add · PROD-1493 edit/stop): all writes ride the fetch-full →
  //    PUT round-trip in useSaveMedication. Edit prefills from the FULL record (the summary
  //    projection is lossy), fetched on Edit-click; Stop is a minimal confirm (date + reason). ──
  const [medDialogOpen, setMedDialogOpen] = useState(false);
  const [medQuery, setMedQuery] = useState('');
  const [medEditDialog, setMedEditDialog] = useState<{
    open: boolean;
    identifier: string | null;
    prefill: MedicationEditPrefill | null;
  }>({ open: false, identifier: null, prefill: null });
  // The identifier whose ANCHORED Edit fetch failed - the anchored surface reads it to dismiss
  // itself (see `DismissOnMount`). Not part of `medEditDialog`: that state means "a prefill is
  // ready or on its way", and a failure is neither.
  const [medEditFailed, setMedEditFailed] = useState<string | null>(null);
  const [medStopDialog, setMedStopDialog] = useState<{ open: boolean; identifier: string | null; display: string }>(
    { open: false, identifier: null, display: '' },
  );
  // Both tiers, favourites first (use-medication-search-tiered.ts). Favourites are per provider,
  // so a nurse recording a reported medication usually has none; favourites alone found nothing.
  const medSearch = useMedicationSearchTiered(medPerformerId, medQuery);
  const saveMedication = useSaveMedication(patientId);

  // Inline drug-interaction check (PROD parity — Flutter getDrugInteractions). The add/edit dialog
  // reports the picked med here; we run the interaction engine for it (subject = patient lets the
  // engine cross-reference the patient's persisted active meds). Diagnoses aren't in scope on the
  // rail (that's the encounter A/P), so only drug-drug / drug-allergy alerts surface here.
  const [medForDi, setMedForDi] = useState<{ code: string; display: string; system: string } | null>(null);
  const drugInteractions = useDrugInteractions(
    medForDi
      ? buildDrugInteractionInput({
          patientId,
          providerId: medPerformerId,
          medications: [{ code: medForDi.code, display: medForDi.display, system: medForDi.system, quantity: '1' }],
        })
      : null,
    medsInteractive && !!medForDi,
  );

  // ── Allergies (PROD-1265): add + edit + soft-delete. Full chart records (with recorder)
  //    come from the chart-detail list — the summary projection lacks them. ──
  const [allergyDialog, setAllergyDialog] = useState<{ open: boolean; editing: ChartAllergy | null }>(
    { open: false, editing: null },
  );
  // Anchored-popover twin of `allergyDialog.open` (the `flagPopoverOpen` precedent): the
  // pre-encounter `+` renders this dialog inside the rail popover, where the host never sets
  // `allergyDialog`, so every query gated on "is the form up" has to see the popover too.
  const [allergyPopoverOpen, setAllergyPopoverOpen] = useState(false);
  const allergyFormOpen = allergyDialog.open || allergyPopoverOpen;
  const [allergyQuery, setAllergyQuery] = useState('');
  // PROD-1998 Drug / Non-drug: the dialog reports its toggle; only the matching source is enabled,
  // so a non-drug search never fires the medication request and vice versa.
  const [allergyKind, setAllergyKind] = useState<AllergenKind>('non-drug');
  const allergyCodeSearch = useAllergyCodeSearch(allergyQuery, allergyFormOpen && allergyKind === 'non-drug');
  const drugAllergenSearch = useMedicationValueSetSearch(allergyQuery, allergyFormOpen && allergyKind === 'drug');
  const severities = useAllergenSeverities(allergyFormOpen);
  // Reaction is a MANDATORY field on Flutter's allergy form; the rail never offered it, so an
  // allergy added here stored `reaction: null`. Gated on the dialog being open, like severities.
  const allergenReactions = useAllergenReactions(allergyFormOpen);
  const allergyRecords = usePatientAllergies(interactive ? patientId : undefined);
  const saveAllergy = useSaveAllergy(patientId);

  // ── Problems / Conditions (PROD-1265): add (always Active — the CreateCondition contract) +
  //    edit (incl active↔resolved transition) + soft-delete. Two chart-detail fetches
  //    (Active/Resolved) supply the full records edit/delete need. ──
  const [conditionDialog, setConditionDialog] = useState<{ open: boolean; editing: ChartCondition | null }>(
    { open: false, editing: null },
  );
  const [conditionQuery, setConditionQuery] = useState('');
  // "+ Note" (PROD-1489 BRD row action): a small prefilled-note dialog over the SAME condition
  // update PUT (single scalar note, Flutter parity — see to-condition-request 'note' action).
  const [noteDialog, setNoteDialog] = useState<{ open: boolean; conditionId: string | null }>(
    { open: false, conditionId: null },
  );
  // Rail v3: the pre-visit appends per condition id (`onAppendProblemNote`). `chain` = the notes the server has held because of this
  // page's SUCCESSFUL appends, oldest first (its first item is the note before them); `inFlight` + `queue` keep one request at a time.
  const appendState = useRef(new Map<string, { chain: string[]; inFlight: boolean; queue: string[] }>());
  const [conditionPopoverOpen, setConditionPopoverOpen] = useState(false);
  const dxSearch = useDiagnosisCodeSearch(conditionQuery, conditionDialog.open || conditionPopoverOpen);
  const activeConditions = usePatientConditions(interactive ? patientId : undefined, CONDITION_STATUS.active);
  const resolvedConditions = usePatientConditions(interactive ? patientId : undefined, CONDITION_STATUS.resolved);
  const saveCondition = useSaveCondition(patientId);

  // ── Procedures (PROD-1266): add (coded CPT search) + edit + soft-delete. The full chart record
  //    (with the original asserter edit/delete must preserve) comes from the chart-detail list —
  //    the summary projection omits it. ──
  const [procedureDialog, setProcedureDialog] = useState<{ open: boolean; editing: ChartProcedure | null }>(
    { open: false, editing: null },
  );
  const [procedureQuery, setProcedureQuery] = useState('');
  const [procedurePopoverOpen, setProcedurePopoverOpen] = useState(false);
  const procedureCodeSearch = useProcedureCodeSearch(procedureQuery, procedureDialog.open || procedurePopoverOpen);
  const procedureRecords = usePatientProcedures(interactive ? patientId : undefined);
  const saveProcedure = useSaveProcedure(patientId);

  // ── Immunizations / Vaccines (PROD-1267): IPS-faithful add (vaccine + administered date +
  //    remarks ONLY) + edit + soft-delete. The full chart record (with the original performer)
  //    comes from GetImmunizationByPatientId — edit/delete suppressed until it loads. ──
  const [immunizationDialog, setImmunizationDialog] = useState<{ open: boolean; editing: ChartImmunization | null }>(
    { open: false, editing: null },
  );
  const [vaxQuery, setVaxQuery] = useState('');
  const [immunizationPopoverOpen, setImmunizationPopoverOpen] = useState(false);
  const vaccineCodeSearch = useVaccineCodeSearch(vaxQuery, immunizationDialog.open || immunizationPopoverOpen);
  const immunizationRecords = usePatientImmunizations(interactive ? patientId : undefined);
  const saveImmunization = useSaveImmunization(patientId);

  // ── Family History (PROD-1268): add + edit + soft-delete. A record = ONE relationship (coded
  //    master typeahead) + an ARRAY of ICD-10 diagnosis rows (each description + From/To dates).
  //    The full chart records (per-row recorder/dates) come from GetFamilyMedicalHistoryByPatientId
  //    — the lossy summary projection lacks them. ──
  const [familyDialog, setFamilyDialog] = useState<{ open: boolean; editing: ChartFamilyHistory | null }>(
    { open: false, editing: null },
  );
  const [relationshipQuery, setRelationshipQuery] = useState('');
  const [familyDxQuery, setFamilyDxQuery] = useState('');
  const relationshipSearch = useRelationships({ query: relationshipQuery, category: 'FamilyHistory' });
  const [familyPopoverOpen, setFamilyPopoverOpen] = useState(false);
  const familyDxSearch = useDiagnosisCodeSearch(familyDxQuery, familyDialog.open || familyPopoverOpen);
  const familyRecords = usePatientFamilyHistory(interactive ? patientId : undefined);
  const saveFamilyHistory = useSaveFamilyHistory(patientId);

  // ── Social History (PROD-1269): CODED add (type typeahead + quantity Select + From/To dates +
  //    comment) + edit + soft-delete. The full chart record (with the original `createdBy` the
  //    update body must preserve) comes from GetSocialHistoryByPatientId — edit/delete suppressed
  //    until it loads + carries createdBy (strict recorder guard). NOT the read-only
  //    Occupation/Education SocialSection cards (a separate display, left untouched). ──
  const [socialDialog, setSocialDialog] = useState<{ open: boolean; editing: ChartSocialHistory | null }>(
    { open: false, editing: null },
  );
  const [socialQuery, setSocialQuery] = useState('');
  const [socialPopoverOpen, setSocialPopoverOpen] = useState(false);
  const socialFormOpen = socialDialog.open || socialPopoverOpen;
  const socialTypeSearch = useSocialHistoryCodeSearch(socialQuery, socialFormOpen);
  const socialQtyList = useSocialHabitQtyList(socialFormOpen);
  const socialRecords = usePatientSocialHistory(interactive ? patientId : undefined);
  const saveSocialHistory = useSaveSocialHistory(patientId);

  // ── Occupation / Education (PROD-1272): single-entity PUT upsert from the rail. Source data is the
  //    patient detail (occupationEntity/educationEntity, PROD-1270). Add/edit only — these are one
  //    entity per patient edited in place; there is no delete model. ──
  const [occupationDialogOpen, setOccupationDialogOpen] = useState(false);
  const [educationDialogOpen, setEducationDialogOpen] = useState(false);
  const [educationPopoverOpen, setEducationPopoverOpen] = useState(false);
  const educationLevels = useEducationLevels(educationDialogOpen || educationPopoverOpen);
  const saveOccupation = useSaveOccupation(patientId);
  const saveEducation = useSaveEducation(patientId);

  function onSubmitOccupation(value: OccupationFormValue) {
    if (!patientId) return;
    const req = buildOccupationRequest(value, { patientId, existing: occupationEntity ?? null });
    if (!req) return;
    saveOccupation.mutate(req);
    setOccupationDialogOpen(false);
  }
  function onSubmitEducation(value: EducationFormValue) {
    if (!patientId) return;
    const req = buildEducationRequest(value, { patientId, existing: educationEntity ?? null });
    if (!req) return;
    saveEducation.mutate(req);
    setEducationDialogOpen(false);
  }

  // Close + clear only once the server accepted the medication. On failure the form stays open with
  // everything typed (the dialog catches the rejection) and the error is reported here — the app's
  // QueryClient has no global mutation onError, so nothing else would say it failed.
  function onSaveMedication(input: MedicationFormInput) {
    if (!patientId || !encounterId || !medPerformerId) return undefined;
    return saveMedication
      .mutateAsync({
        kind: 'create',
        body: buildCreateMedicationBody(input, { patientId, encounterId, performerId: medPerformerId }),
      })
      .then(
        () => {
          setMedDialogOpen(false);
          setMedQuery('');
        },
        (err: unknown) => {
          showServerError('Save medication', err);
          throw err;
        },
      );
  }
  // (Medication soft-delete row action removed per the BRD's Edit·Stop 2-item menu, PROD-1493 —
  // the 'delete' variant in useSaveMedication remains for a cheap reinstate.)
  // Edit (PROD-1493): fetch the FULL record first (the summary entry is lossy), map it to the
  // dialog prefill, then open. Errors degrade to not-opening (no half-prefilled clinical form).
  // Latest-click-wins guard: rapid Edit clicks on different rows race their fetches — an older
  // response resolving last must NOT overwrite the newer dialog target (Codex CONFIRMED).
  const medEditSeq = useRef(0);
  //
  // `anchored` = the row's Edit surface is an anchored POPOVER (the pre-encounter rail), so the
  // fetched prefill is parked in this same state with `open: false`: the `dialogs` fragment is
  // gated on `.open` and must stay shut, while the popover renderer reads `.prefill` by
  // identifier. One state, two surfaces — no second copy of the fetch to keep in step.
  async function onEditMedication(identifier: string, anchored = false) {
    const seq = ++medEditSeq.current;
    try {
      const full = await getMedicationDetails(identifier);
      if (seq !== medEditSeq.current) return; // a newer Edit click superseded this fetch
      setMedEditDialog({
        open: !anchored,
        identifier,
        // The whole schedule (times a day, interval, specific days, start date) plus the FULL route
        // coding, so an edit round-trips the record instead of re-asking (Codex CONFIRMED route fix).
        prefill: toMedicationEditPrefill(full),
      });
    } catch (err) {
      // fetch failed → leave the dialog closed (no half-prefilled clinical form), but TELL the
      // user — a silent no-op on a clicked action reads as a bug (CoS review). Retry = re-click.
      if (seq === medEditSeq.current) {
        if (anchored) setMedEditFailed(identifier);
        showServerError('Load medication', err);
      }
    }
  }
  function onSubmitMedEdit(input: MedicationFormInput, identifier: string | null = medEditDialog.identifier) {
    const id = identifier;
    setMedEditDialog({ open: false, identifier: null, prefill: null });
    if (id) saveMedication.mutate({ kind: 'edit', identifier: id, input });
  }
  function onConfirmStopMedication(
    value: { endYyyymmdd: string; reason?: string },
    identifier: string | null = medStopDialog.identifier,
  ) {
    const id = identifier;
    setMedStopDialog({ open: false, identifier: null, display: '' });
    if (id) saveMedication.mutate({ kind: 'stop', identifier: id, ...value });
  }

  // The RAW flag records ARE the list-replace source (PROD-1989). This used to reconstruct them
  // from the rendered 'flags' IpsSection, which drops `flagResponse.code` — the backend then
  // rejected the body with
  //   Error converting value {null} to type 'System.Int32'. Path '[0].flagResponse.code'
  // so adding a SECOND flag and deleting ANY flag both failed (adding the first worked, because
  // the retained list was empty). Soft-deleted rows are passed through UNFILTERED so the
  // un-delete branch in buildCreateFlagBody can match an existing code instead of appending a
  // duplicate — with a codeless list that branch could never fire.
  function currentFlagList(): PatientFlag[] {
    return flagRecords ?? [];
  }
  // A flag we cannot represent must not be sent: the request REPLACES the whole list, so a dropped
  // or nulled entry deletes a live safety flag (No Blood Products / No Narcotics) server-side.
  // buildCreateFlagBody throws FlagCodeMissingError; surface it instead of posting a bad body.
  function saveFlagAction(action: Parameters<typeof buildCreateFlagBody>[1]) {
    try {
      saveFlags.mutate(
        buildCreateFlagBody(currentFlagList(), action, {
          patientId: patientId!,
          userId: currentUserId,
          nowIso: new Date().toISOString(),
        }),
      );
    } catch (e) {
      showServerError('Could not update patient flags', e);
    }
  }
  function onAddFlag(category: FlagCategory) {
    saveFlagAction({ type: 'add', category });
    setFlagDialogOpen(false);
  }
  function onDeleteFlag(identifier: string) {
    saveFlagAction({ type: 'delete', identifier });
  }

  // ── Allergy handlers ──────────────────────────────────────────────────────
  // Drug mode: medications become allergen rows carrying DRUG_ALLERGY_CATEGORY, so both modes share
  // one results mapper and one resolver.
  const allergenOptions: AllergyCodeOption[] =
    allergyKind === 'drug'
      ? (drugAllergenSearch.data ?? []).map(medicationToAllergen)
      : (allergyCodeSearch.data ?? []);
  const allergyResults = allergyOptionsToCodeSearchResults(allergenOptions);
  const allergySearching = allergyKind === 'drug' ? drugAllergenSearch.isFetching : allergyCodeSearch.isFetching;
  function resolveAllergen(r: CodeSearchResult): AllergyCodeOption | null {
    return (
      allergenOptions.find((o) => (r.code ? o.code === r.code : (o.display ?? '').trim() === r.display)) ?? null
    );
  }
  function findAllergy(identifier: string): ChartAllergy | undefined {
    return allergyRecords.data?.find((a) => a.identifier === identifier);
  }
  function closeAllergyDialog() {
    setAllergyDialog({ open: false, editing: null });
    setAllergyQuery('');
    setAllergyKind('non-drug');
  }
  // The ANCHORED row surfaces pass their record EXPLICITLY (second argument) instead of routing
  // through the host dialog state. Two reasons, and the first is the whole point of this change:
  // the `dialogs` fragment below renders off `xDialog.open`, so an anchored edit that set that
  // state would raise a CENTRED modal beside its popover — exactly what Ahmed asked to be gone
  // from the left column. Second, the record is already in hand at the row, so threading it is
  // shorter than a round trip through state. The default keeps every existing caller (`dialogs`,
  // the in-encounter banner) reading the state exactly as before.
  function onSubmitAllergy(value: AllergyFormValue, editing: ChartAllergy | null = allergyDialog.editing) {
    if (!patientId || !currentUserId) return;
    const action = editing
      ? ({ type: 'edit', existing: editing, value } as const)
      : ({ type: 'add', value } as const);
    const req = buildAllergyRequest(action, { patientId, userId: currentUserId, encounterId });
    if (req) saveAllergy.mutate(req);
    closeAllergyDialog();
  }
  function onEditAllergy(identifier: string) {
    const existing = findAllergy(identifier);
    if (existing) setAllergyDialog({ open: true, editing: existing });
  }
  // (Allergy row Delete removed per the BRD's "Edit only (inline pencil)" taxonomy row, PROD-1489 —
  // buildAllergyRequest's 'delete' action + useSaveAllergy still support a cheap reinstate.)

  // ── Condition handlers ────────────────────────────────────────────────────
  // Master DiagnosisCodes ships rows WRAPPED as { code: <real Coding> } — flatten at the boundary
  // so the picked code is a flat ConditionCode (not nested → backend 400). See flattenDiagnosisRow.
  const dxCodes: ConditionCode[] = (dxSearch.data ?? []).map(flattenDiagnosisRow);
  const conditionResults = toCodeSearchResults(dxCodes);
  function resolveCode(r: CodeSearchResult): ConditionCode | null {
    return dxCodes.find((c) => (r.code ? c.code === r.code : (c.display ?? '').trim() === r.display)) ?? null;
  }
  function findCondition(identifier: string): ChartCondition | undefined {
    return [...(activeConditions.data ?? []), ...(resolvedConditions.data ?? [])].find(
      (c) => c.identifier === identifier,
    );
  }
  function closeConditionDialog() {
    setConditionDialog({ open: false, editing: null });
    setConditionQuery('');
  }
  function onSubmitCondition(value: ConditionFormValue, editing: ChartCondition | null = conditionDialog.editing) {
    if (!patientId || !currentUserId) return;
    const action = editing
      ? ({ type: 'edit', existing: editing, value } as const)
      : ({ type: 'add', value } as const);
    const req = buildConditionRequest(action, { patientId, userId: currentUserId, userName: currentUserName, encounterId });
    if (req) saveCondition.mutate(req);
    closeConditionDialog();
  }
  function onAddCondition() {
    setConditionDialog({ open: true, editing: null });
  }
  function onEditCondition(identifier: string) {
    const existing = findCondition(identifier);
    if (existing) setConditionDialog({ open: true, editing: existing });
  }
  function onDeleteCondition(identifier: string) {
    const existing = findCondition(identifier);
    if (!patientId || !existing) return;
    const req = buildConditionRequest({ type: 'delete', existing }, { patientId, userId: currentUserId ?? '', encounterId });
    if (req) saveCondition.mutate(req);
  }
  // One-click Resolve (BRD row action for Active Problems, PROD-1489): flip Active → Resolved with
  // today as the resolution date, via the builder's `resolve` action (round-trips the full record —
  // no field drift). Distinct from Edit (which opens the dialog).
  function onResolveCondition(identifier: string) {
    const existing = findCondition(identifier);
    if (!patientId || !currentUserId || !existing?.identifier) return;
    // Resolution date = today in LOCAL calendar time (mirrors the edit dialog's toYyyymmdd). NOT
    // toISOString().slice(0,10), which is UTC and records the wrong clinical date near midnight in
    // +offset time zones (e.g. early-morning local resolves to "yesterday"). Codex MAJOR.
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const todayLocal = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const req = buildConditionRequest(
      { type: 'resolve', existing, endYyyymmdd: todayLocal },
      { patientId, userId: currentUserId, userName: currentUserName, encounterId },
    );
    if (req) saveCondition.mutate(req);
  }
  // One-click Unresolve — the counterpart Flutter has on every surface that lists resolved problems:
  // the chart tab (`problems_tab.dart:325-332`, string `S.of(context).unresolve`) AND this very rail
  // (`clinical_info_widget.dart:388-406` → `AppConstants.actionUnResolveProblem`,
  // `app_constants.dart:201`). React's chart tab already implements it
  // (`problems-chart-tab.tsx` confirmUnresolve); only the rail was missing it, which is why a
  // clinician could resolve a problem here and then have no way to put it back.
  function onUnresolveCondition(identifier: string) {
    const existing = findCondition(identifier);
    if (!patientId || !currentUserId || !existing?.identifier) return;
    const req = buildConditionRequest(
      { type: 'unresolve', existing },
      { patientId, userId: currentUserId, userName: currentUserName, encounterId },
    );
    if (req) saveCondition.mutate(req);
  }
  // "+ Note" (BRD row action): open the prefilled note dialog for one condition; save writes the
  // scalar note through the same update PUT, preserving every other field ('note' action).
  function onOpenNote(identifier: string) {
    if (findCondition(identifier)) setNoteDialog({ open: true, conditionId: identifier });
  }
  function onSubmitNote(
    note: string,
    existing: ChartCondition | undefined = noteDialog.conditionId ? findCondition(noteDialog.conditionId) : undefined,
  ) {
    setNoteDialog({ open: false, conditionId: null });
    if (!patientId || !currentUserId || !existing) return;
    const req = buildConditionRequest(
      { type: 'note', existing, note },
      { patientId, userId: currentUserId, userName: currentUserName, encounterId },
    );
    if (req) saveCondition.mutate(req);
  }

  // ── Rail v3 row handlers (left-rail target 4 Nov §3 / §4). Plain callbacks keyed by row id, so the catalog's inline RailNote
  //    and hover card (the assembly step) can call them with what the clinician typed. ─────────────────────────────────────────
  /** Pre-visit "Add a note" on a problem: APPENDS "<text> — <author>, <D-45 date>" to the record's note; never overwrites.
   *  Each append is a full-record PUT, so they go ONE AT A TIME per problem (two at once could land out of order, and the later-landing
   *  shorter one would drop the other — Codex P1, 4 Nov); an append typed while one is saving waits for it. Each is built on the
   *  latest note this page knows the server holds: the record as read, or — while the read has not caught up with this page's own
   *  successful appends — the last one written. A record that changed in any other way (cleared, edited elsewhere) is built on as
   *  read and the page forgets its own chain; a failed save forgets it too, so failed text is never written back (Codex P1, 4 Nov). */
  function onAppendProblemNote(identifier: string, typedText: string) {
    const st = appendState.current.get(identifier) ?? { chain: [], inFlight: false, queue: [] };
    appendState.current.set(identifier, st);
    if (st.inFlight) {
      st.queue.push(typedText);
      return;
    }
    sendProblemAppend(identifier, typedText, st);
  }
  function sendProblemAppend(identifier: string, typedText: string, st: { chain: string[]; inFlight: boolean; queue: string[] }) {
    if (!patientId || !currentUserId) {
      st.queue = [];
      return;
    }
    st.inFlight = true;
    // Built on the record as the server holds it NOW: both condition lists are read again before every append, so a note cleared or
    // edited elsewhere (or by this page) is never written back from a cached copy (Codex P1 on #961, 4 Nov). `chain` stays unused.
    // `mutateAsync`, not `mutate` with per-call callbacks: those fire only for the observer's LAST call, and another condition save in
    // between (a Resolve) would leave this append "in flight" for ever.
    // `throwOnError`: a failed read rejects (react-query would otherwise resolve with the cached copy), so the append is abandoned
    // rather than built on stale text (Codex, second pass on #961).
    Promise.all([activeConditions.refetch({ throwOnError: true }), resolvedConditions.refetch({ throwOnError: true })])
      .then(([active, resolved]) => {
        const existing = [...(active.data ?? []), ...(resolved.data ?? [])].find((c) => c.identifier === identifier);
        if (!existing) return undefined;
        const req = buildConditionAppendNoteRequest(
          existing,
          typedText,
          { name: currentUserName, atIso: new Date().toISOString() },
          { patientId, userId: currentUserId, userName: currentUserName, encounterId },
        );
        return req ? saveCondition.mutateAsync(req) : undefined;
      })
      .then(() => {
        st.inFlight = false;
        const next = st.queue.shift();
        if (next !== undefined) sendProblemAppend(identifier, next, st);
      })
      .catch((err: unknown) => {
        st.inFlight = false;
        st.queue = [];
        showServerError('Add a note', err);
      });
  }
  /** Undo resolve (ruling 3, "it was a mistake"): Active again with the stored onset, never the Reactivate onset stamp. */
  function onUndoResolveCondition(identifier: string) {
    const existing = findCondition(identifier);
    if (!patientId || !currentUserId || !existing?.identifier) return;
    const req = buildConditionRequest({ type: 'undo-resolve', existing }, { patientId, userId: currentUserId, userName: currentUserName, encounterId });
    if (req) saveCondition.mutate(req);
  }
  /** "Entered in error" on a problem, with its required reason (Scrips.Patient T4). No reason → nothing is sent. */
  function onMarkEnteredInError(identifier: string, reason: string) {
    if (!patientId || !currentUserId || !findCondition(identifier)) return;
    const req = buildConditionEnteredInErrorRequest(identifier, reason);
    if (req) saveCondition.mutate(req);
  }
  // ── "Add to note" (left-rail target §4b round 4, §4d, §7.2): ONE handler, `addToNote(rowId, targetId, text?)`, and the targets a
  // row offers, `railNoteTargets(rowId)` (rail-v3/rail-note-targets.ts). HPI and ROS are a chart piece in that note line (the
  // page's `notePieces` host); diagnoses are the page's `handleAddDx` (`onAddProblemToNote`); Assessment text has no landing yet.
  /** A landing's state for a record: a note line through the page's host (no host → there is no encounter note here: pre-visit or
   *  a legacy host), the diagnoses through the page's `handleAddDx` path when the host passed it. */
  const railLanding = (landing: RailTargetLanding, sourceId: string) => {
    if (landing === 'diagnoses') return onAddProblemToNote ? LANDING_READY : LANDING_ABSENT;
    if (notePieces) return notePieces.state(landing, sourceId);
    return landing === 'assessment' ? LANDING_ABSENT : landingBlocked(NO_NOTE_REASON);
  };
  /** The targets a row offers now and, when none, why (the disabled reason). Undefined for a row that has no "Add to note". */
  function railNoteTargets(sectionId: string, rowId: string): RailNoteTargets | undefined {
    const kind = RAIL_NOTE_SECTION_KIND[sectionId];
    if (!kind) return undefined;
    const icd10 = kind === 'problem' ? (findCondition(rowId)?.code?.code ?? findIpsEntry(sectionId, rowId)?.code) : undefined;
    return targetsFor({ kind, sourceId: rowId, icd10 }, clinicalRole, { landing: railLanding });
  }
  /** The row's note line (§4): a problem as the persona reads it (ruling 6), a medication with its dose as recorded, an allergy
   *  as the row shows it. Undefined when the row is not found. */
  function railNoteLine(kind: RailNoteRowKind, sectionId: string, rowId: string): string | undefined {
    const entry = findIpsEntry(sectionId, rowId);
    if (kind === 'medication') return entry ? medicationNoteLine({ name: entry.primary, dose: entry.secondary }) : undefined;
    if (kind === 'allergy') return entry ? allergyNoteLine({ name: entry.primary, detail: entry.secondary }) : undefined;
    const rec = findCondition(rowId);
    if (!rec && !entry) return undefined;
    return problemNoteLine(
      {
        name: entry?.primary ?? rec?.code?.display ?? '',
        code: rec?.code?.code ?? entry?.code,
        clinicalStatus: rec?.clinicalStatus,
        // The condition record carries no verificationStatus today (authority: accepted known gap): "not recorded".
        verificationStatus: (rec as { verificationStatus?: string | null } | undefined)?.verificationStatus,
      },
      clinicalRole === 'physician' ? 'physician' : 'other',
    );
  }
  const RAIL_NOTE_QUERY = { problem: 'problems.active', allergy: 'allergies.list', medication: 'medications.active' } as const;
  /** "Add to note" to ONE picked target: each of its landings in order — a note line gets ONE piece (the typed text with the first
   *  line only, so it is never written twice), the diagnoses get the problem through `handleAddDx`. A target the row does not
   *  offer now writes nothing (a stale pick, a duplicate, a landing that went away). Never a fallback to another place. */
  function addToNote(sectionId: string, rowId: string, targetId: RailNoteTargetId, typedText?: string) {
    const kind = RAIL_NOTE_SECTION_KIND[sectionId];
    const target = railNoteTargets(sectionId, rowId)?.targets.find((t) => t.id === targetId);
    if (!kind || !target) return;
    let typed = typedText;
    for (const landing of target.lands) {
      if (landing === 'diagnoses') {
        const rec = findCondition(rowId);
        const problem = rec ? toPatientProblems([rec])[0] : undefined;
        if (problem && onAddProblemToNote) onAddProblemToNote(problemToCodeSearchResult(problem));
        continue;
      }
      const display = railNoteLine(kind, sectionId, rowId);
      if (!notePieces || display === undefined) continue;
      notePieces.insert(landing, railNotePiece({ query: RAIL_NOTE_QUERY[kind], display, sourceId: rowId, patientId, encounterId }), typed);
      typed = undefined;
    }
  }
  /** The action's own click (no picker): a row with one possible target (a medication) adds to it at once; any other row waits
   *  for the clinician's pick in the row's note (UC-30 rule 2: no default target). */
  function addToSoleTarget(sectionId: string, rowId: string) {
    const kind = RAIL_NOTE_SECTION_KIND[sectionId];
    const offered = railNoteTargets(sectionId, rowId);
    const sole = kind && offered ? soleTarget({ kind }, offered) : undefined;
    if (sole) addToNote(sectionId, rowId, sole.id);
  }
  // Undo resolve is offered only right after a resolve made on this page (ruling 3); a reload or a Reactivate clears it.
  const [justResolved, setJustResolved] = useState<ReadonlySet<string>>(() => new Set());
  const markResolved = (identifier: string, on: boolean) =>
    setJustResolved((prev) => {
      const next = new Set(prev);
      if (on) next.add(identifier);
      else next.delete(identifier);
      return next;
    });

  // ── Procedure handlers ────────────────────────────────────────────────────
  // CPT master rows can ship WRAPPED as { code: <Coding> } (same hazard as DiagnosisCodes) —
  // normalize ONCE here so both the picker options AND resolveProcedure match on the flat shape.
  // (Matching against the raw rows would fail for wrapped rows: o.code is an object, displayName
  // absent → resolves to null and no code is selected. Codex MAJOR, PROD-1266.) The picker reads
  // displayName (NOT display).
  const procedureCodes = (procedureCodeSearch.data ?? [])
    .map((c) => normalizeProcedureCode(c))
    .filter((c): c is CodeDisplayName => !!c);
  const procedureResults = procedureOptionsToCodeSearchResults(procedureCodes);
  function resolveProcedure(r: CodeSearchResult): CodeDisplayName | null {
    return (
      procedureCodes.find((c) => (r.code ? c.code === r.code : (c.displayName ?? '').trim() === r.display)) ?? null
    );
  }
  function findProcedure(identifier: string): ChartProcedure | undefined {
    return procedureRecords.data?.find((p) => p.identifier === identifier);
  }
  function closeProcedureDialog() {
    setProcedureDialog({ open: false, editing: null });
    setProcedureQuery('');
  }
  function onSubmitProcedure(value: ProcedureFormValue, editing: ChartProcedure | null = procedureDialog.editing) {
    if (!patientId || !currentUserId) return;
    const action = editing
      ? ({ type: 'edit', existing: editing, value } as const)
      : ({ type: 'add', value } as const);
    const req = buildProcedureRequest(action, { patientId, userId: currentUserId, encounterId });
    if (req) saveProcedure.mutate(req);
    closeProcedureDialog();
  }
  function onAddProcedure() {
    setProcedureDialog({ open: true, editing: null });
  }
  function onEditProcedure(identifier: string) {
    const existing = findProcedure(identifier);
    if (existing) setProcedureDialog({ open: true, editing: existing });
  }
  function onDeleteProcedure(identifier: string) {
    const existing = findProcedure(identifier);
    if (!patientId || !existing) return;
    const req = buildProcedureRequest({ type: 'delete', existing }, { patientId, userId: currentUserId ?? '', encounterId });
    if (req) saveProcedure.mutate(req);
  }

  // ── Immunization handlers ─────────────────────────────────────────────────
  // Master VaccinationCodesList rows can arrive WRAPPED ({code:<Coding>}) — normalize ONCE here so
  // both the picker options AND resolveVaccine match on the flat shape (the procedures MAJ lesson).
  const vaccineCodes: ImmunizationCoding[] = (vaccineCodeSearch.data ?? [])
    .map((c) => normalizeVaccineCoding(c))
    .filter((c): c is ImmunizationCoding => c !== null);
  const immunizationResults = vaccineOptionsToCodeSearchResults(vaccineCodes);
  function resolveVaccine(r: CodeSearchResult): ImmunizationCoding | null {
    return (
      vaccineCodes.find((c) => (r.code ? c.code === r.code : (c.display ?? '').trim() === r.display)) ?? null
    );
  }
  function findImmunization(identifier: string): ChartImmunization | undefined {
    return immunizationRecords.data?.find((v) => v.identifier === identifier);
  }
  function closeImmunizationDialog() {
    setImmunizationDialog({ open: false, editing: null });
    setVaxQuery('');
  }
  function onSubmitImmunization(
    value: ImmunizationFormValue,
    editing: ChartImmunization | null = immunizationDialog.editing,
  ) {
    if (!patientId || !currentUserId) return;
    const action = editing
      ? ({ type: 'edit', existing: editing, value } as const)
      : ({ type: 'add', value } as const);
    const req = buildImmunizationRequest(action, { patientId, userId: currentUserId, encounterId });
    if (req) saveImmunization.mutate(req);
    closeImmunizationDialog();
  }
  function onAddImmunization() {
    setImmunizationDialog({ open: true, editing: null });
  }
  function onEditImmunization(identifier: string) {
    const existing = findImmunization(identifier);
    if (existing) setImmunizationDialog({ open: true, editing: existing });
  }
  function onDeleteImmunization(identifier: string) {
    const existing = findImmunization(identifier);
    if (!patientId || !existing) return;
    const req = buildImmunizationRequest({ type: 'delete', existing }, { patientId, userId: currentUserId ?? '', encounterId });
    if (req) saveImmunization.mutate(req);
  }

  // ── Family-history handlers ───────────────────────────────────────────────
  // Relationship typeahead (NAB041 master) → CodeSearchResult[]. resolveRelationship maps a clicked
  // row back to the coded {code,name,system} the wire body needs.
  const relationshipResults: CodeSearchResult[] = (relationshipSearch.data ?? []).flatMap((r, i) => {
    const display = (r.name ?? r.code ?? '').trim();
    if (!display) return [];
    return [{ id: r.code ?? `rel-${i}-${display}`, code: r.code, display, system: r.system }];
  });
  function resolveRelationship(r: CodeSearchResult): FamilyHistoryRelationship | null {
    const opts = relationshipSearch.data ?? [];
    const match = opts.find((o) => (r.code ? o.code === r.code : (o.name ?? '').trim() === r.display));
    if (!match) return null;
    return { code: match.code ?? null, name: match.name ?? null, system: match.system ?? null };
  }
  // ICD-10 rows ship WRAPPED ({ code: <Coding> }) — flatten at the boundary (flattenDiagnosisRow)
  // so the picked code is a flat ConditionCode (not nested → backend 400). Mirrors Conditions.
  const familyDxCodes: ConditionCode[] = (familyDxSearch.data ?? []).map(flattenDiagnosisRow);
  const familyDxResults = toCodeSearchResults(familyDxCodes);
  function resolveFamilyDx(r: CodeSearchResult): ConditionCode | null {
    return familyDxCodes.find((c) => (r.code ? c.code === r.code : (c.display ?? '').trim() === r.display)) ?? null;
  }
  function findFamilyHistory(identifier: string): ChartFamilyHistory | undefined {
    return familyRecords.data?.find((f) => f.identifier === identifier);
  }
  function closeFamilyDialog() {
    setFamilyDialog({ open: false, editing: null });
    setRelationshipQuery('');
    setFamilyDxQuery('');
  }
  function onSubmitFamilyHistory(
    value: FamilyHistoryFormValue,
    editing: ChartFamilyHistory | null = familyDialog.editing,
  ) {
    if (!patientId || !currentUserId) return;
    const action = editing
      ? ({ type: 'edit', existing: editing, value } as const)
      : ({ type: 'add', value } as const);
    const req = buildFamilyHistoryRequest(action, { patientId, userId: currentUserId, encounterId });
    if (req) saveFamilyHistory.mutate(req);
    closeFamilyDialog();
  }
  function onEditFamilyHistory(identifier: string) {
    const existing = findFamilyHistory(identifier);
    if (existing) setFamilyDialog({ open: true, editing: existing });
  }
  function onDeleteFamilyHistory(identifier: string) {
    const existing = findFamilyHistory(identifier);
    if (!patientId || !existing) return;
    const req = buildFamilyHistoryRequest({ type: 'delete', existing }, { patientId, userId: currentUserId ?? '', encounterId });
    if (req) saveFamilyHistory.mutate(req);
  }

  // ── Social History handlers ───────────────────────────────────────────────
  // The type typeahead is a master SocialHistoryCodeRow[] (carrying the nested category). Surface
  // the rows to the picker, then resolve a clicked result id back to the FULL row (with category)
  // so the create body can preserve the nested + top-level category (matching AddSocialHistoryEditor).
  const socialTypeResults = socialTypeOptionsToCodeSearchResults(socialTypeSearch.data);
  function resolveSocialType(r: CodeSearchResult): SocialHistoryCodeRow | null {
    const opts = socialTypeSearch.data ?? [];
    return opts.find((o) => (r.code ? o.code === r.code : (o.display ?? '').trim() === r.display)) ?? null;
  }
  function findSocialHistory(identifier: string): ChartSocialHistory | undefined {
    return socialRecords.data?.find((h) => h.identifier === identifier);
  }
  function closeSocialDialog() {
    setSocialDialog({ open: false, editing: null });
    setSocialQuery('');
  }
  function onSubmitSocialHistory(
    value: SocialHistoryFormValue,
    editing: ChartSocialHistory | null = socialDialog.editing,
  ) {
    if (!patientId || !currentUserId) return;
    const action = editing
      ? ({ type: 'edit', existing: editing, value } as const)
      : ({ type: 'add', value } as const);
    const req = buildSocialHistoryRequest(action, { patientId, userId: currentUserId, encounterId });
    if (req) saveSocialHistory.mutate(req);
    closeSocialDialog();
  }
  function onAddSocialHistory() {
    setSocialDialog({ open: true, editing: null });
  }
  function onEditSocialHistory(identifier: string) {
    const existing = findSocialHistory(identifier);
    if (existing) setSocialDialog({ open: true, editing: existing });
  }
  // (Social-history row Delete removed per the BRD's explicit Edit-only line, PROD-1489 — the
  // builder's 'delete' action + useSaveSocialHistory still support it if Samer reinstates.)

  // Flags are writable only once the RAW records have loaded (PROD-1989) — the body needs their
  // numeric codes. `undefined` = summary still loading; `[]` = loaded with no flags, which MUST stay
  // writable or the first flag can never be added. So the gate is "is it an array", not "is it
  // non-empty".
  const flagsWritable = interactive && Array.isArray(flagRecords);
  const findIpsEntry = (sectionId: string, entryId: string) => findIpsEntryIn(ipsSections, sectionId, entryId);

  // Rail v3 (left-rail target §3): what this user may do here, from the existing server-backed gates the host already applied.
  const railV3Ctx: RailV3ActionContext = {
    stage: railV3Stage,
    persona: clinicalRole,
    mayPlanMedications: !!onMedicationToPlan && railV3Stage === 'started',
    mayStopMedication: canStopMedication,
    apWriter: !!onAddProblemToNote,
    mayReconcile: !!onReconcileMedications,
  };
  // The problem whose "Entered in error" reason is being asked for (the centred surface, when the row's anchored one is not used).
  const [errorDialog, setErrorDialog] = useState<string | null>(null);

  const sections = interactive
    ? ipsSections.map((s) => {
        // Word-labeled actions on EVERY section (PROD-1588 unified anatomy — no glyph exceptions);
        // sections on the Flutter-fallback action set (uniform Edit + Delete, ticket comment
        // 2026-07-06) still pass labeled `actions[]` for the visual taxonomy.
        if (s.id === 'flags') {
          // Records not loaded → display-only. Offering Add/Remove here would POST a list-replace
          // body with `code: null`, which the backend rejects (PROD-1989).
          if (!flagsWritable) return s;
          return {
            ...s,
            onAdd: () => setFlagDialogOpen(true),
            // Flags have no edit flow (a flag is a picked coded category; add/soft-delete only,
            // PROD-1255) — Delete is the whole fallback set here.
            entries: s.entries.map((e) => ({
              ...e,
              actions: [{ label: 'Remove', onClick: () => onDeleteFlag(e.id), tone: 'danger' as const }],
            })),
          };
        }
        if (s.id === 'medications' && medsInteractive && railV3Rows) {
          // Rail v3, left-rail target §3 (Medications). A stopped row keeps the mapper's empty action list.
          const plan = railV3MedicationActions(railV3Ctx);
          return {
            ...s,
            onAdd: () => setMedDialogOpen(true),
            entries: s.entries.map((e) =>
              isMoreTrailer(s.id, e) || e.actions
                ? e
                : {
                    ...e,
                    actions: railV3BindActions(
                      plan,
                      {
                        // Each intent reaches its own handler (#906 path, the page's `medicationToPlan`).
                        // Stop is not planned on the rail until a pending discontinue line exists (rail-v3-actions.ts).
                        ...(onMedicationToPlan
                          ? {
                              Refill: () => onMedicationToPlan(e.id, 'refill'),
                              'Edit dose': () => onMedicationToPlan(e.id, 'change'),
                            }
                          : {}),
                        ...(onReconcileMedications ? { 'Reconcile medications': onReconcileMedications } : {}),
                        'Add to note': () => addToSoleTarget('medications', e.id),
                        Edit: () => void onEditMedication(e.id),
                      },
                      { 'Add to note': railNoteTargets('medications', e.id)?.reason },
                    ),
                  },
            ),
          };
        }
        if (s.id === 'medications' && medsInteractive) {
          return {
            ...s,
            onAdd: () => setMedDialogOpen(true),
            // BRD taxonomy: "Medications (rail/summary) | Edit, Stop | 2-item menu" (PROD-1493).
            // Entry id = the medication identifier (to-ips-sections); the synthetic "+N more"
            // trailer is NOT a real record → no actions. Edit fetches the full record then opens
            // the prefilled dialog; Stop (danger — red hover, DS 1.40.1) opens the minimal
            // confirm. The interim Remove (soft-delete) row action is dropped per the 2-item
            // spec; the 'delete' save variant remains for a cheap reinstate.
            entries: s.entries.map((e) =>
              // Respect a mapper-decided action set: to-ips-sections marks STOPPED meds with an
              // explicitly-empty actions[] (no Edit/Stop on an already-stopped row) — don't
              // overwrite it. The trailer guard stays for the same reason as everywhere else.
              isMoreTrailer(s.id, e) || e.actions
                ? e
                : {
                    ...e,
                    actions: [
                      // `medActions`: Continue · Refill · Change land in the A&P Plan (started encounter, may order; see the prop).
                      ...(onMedicationToPlan && started
                        ? [
                            { label: 'Continue', onClick: () => onMedicationToPlan(e.id, 'continue') },
                            { label: 'Refill', onClick: () => onMedicationToPlan(e.id, 'refill') },
                            { label: 'Change', onClick: () => onMedicationToPlan(e.id, 'change') },
                          ]
                        : []),
                      { label: 'Edit', onClick: () => void onEditMedication(e.id) },
                      // PROD-2669: stopping a prescription is physician-only. A non-physician
                      // keeps Edit (medication reconciliation) and gets no Stop at all.
                      ...(canStopMedication
                        ? [
                            {
                              label: 'Stop',
                              onClick: () => setMedStopDialog({ open: true, identifier: e.id, display: e.primary }),
                              tone: 'danger' as const,
                            },
                          ]
                        : []),
                    ],
                  },
            ),
          };
        }
        if (s.id === 'allergies' && clinicalWriteEnabled) {
          return {
            ...s,
            // Action SET stays "Edit only" (PROD-1489 BRD taxonomy row). The REVEAL is no longer
            // special-cased: PROD-1588 (Samer live review 2026-07-23) unified ONE row-action
            // anatomy across the rail — Allergies hover/focus-reveals a word-labeled Edit exactly
            // like every sibling section. The former `actionReveal: 'inline'` + icon-only ✎ are
            // deprecated no-ops in DS ≥1.55 and are dropped here. Row ✕ delete stays dropped per
            // spec; the delete builder path + handler survive for a cheap reinstate.
            onAdd: () => setAllergyDialog({ open: true, editing: null }),
            // Edit needs the FULL chart record AND its original recorder (buildAllergyRequest
            // blocks otherwise). Suppress the affordance until that record is loaded + has a
            // recorder, so a clinician never clicks a control that would silently no-op.
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              const rec = findAllergy(e.id);
              if (!rec?.recorder) return e;
              return {
                ...e,
                actions: [{ label: 'Edit', onClick: () => onEditAllergy(e.id) }],
              };
            }),
          };
        }
        // Active Problems — the full BRD taxonomy (PROD-1489): Edit · Resolve · + Note. Resolve is
        // one-click (status → Resolved, today's date); + Note opens the prefilled single-note
        // dialog (Flutter-parity scalar note — the append-only reading was never in the spec).
        // Actions suppressed until the full chart record is loaded (all three need it).
        if (s.id === 'active-problems' && clinicalWriteEnabled && railV3Rows) {
          // Rail v3, left-rail target §3 (Problems, active). Actions wait for the full chart record, as below.
          const plan = railV3ProblemActions(railV3Ctx);
          return {
            ...s,
            onAdd: () => onAddCondition(),
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              const rec = findCondition(e.id);
              if (!rec) return e;
              const problem = toPatientProblems([rec])[0];
              return {
                ...e,
                actions: railV3BindActions(
                  plan,
                  {
                    // Pre-visit: a comment on the record, appended (its surface is the actionPopover below).
                    '+ Note': () => onOpenNote(e.id),
                    Resolve: () => {
                      onResolveCondition(e.id);
                      markResolved(e.id, true);
                    },
                    Edit: () => onEditCondition(e.id),
                    // Started: the clinician picks the target in the row's note (§4b round 4); the click alone writes nothing.
                    'Add to note': () => addToSoleTarget('active-problems', e.id),
                    // The page's `handleAddDx` path (`onAddProblemToNote`); a problem with no code cannot be added.
                    ...(onAddProblemToNote && problem
                      ? { 'Add to encounter diagnoses': () => onAddProblemToNote(problemToCodeSearchResult(problem)) }
                      : {}),
                    // Required reason: its surface is the actionPopover below; the click alone sends nothing.
                    'Entered in error': () => setErrorDialog(e.id),
                  },
                  { 'Add to note': railNoteTargets('active-problems', e.id)?.reason },
                ),
              };
            }),
          };
        }
        if (s.id === 'resolved-problems' && clinicalWriteEnabled && railV3Rows) {
          return {
            ...s,
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              if (!findCondition(e.id)) return e;
              return {
                ...e,
                actions: railV3BindActions(railV3ResolvedActions({ justResolved: justResolved.has(e.id), canResolve: clinicalRole === 'physician' }), {
                  Unresolve: () => {
                    onUnresolveCondition(e.id);
                    markResolved(e.id, false);
                  },
                  // Undo resolve (ruling 3): back to Active as it was, the onset kept; offered only right after the resolve.
                  'Undo resolve': () => {
                    onUndoResolveCondition(e.id);
                    markResolved(e.id, false);
                  },
                  Edit: () => onEditCondition(e.id),
                  Remove: () => onDeleteCondition(e.id),
                }),
              };
            }),
          };
        }
        if (s.id === 'active-problems' && clinicalWriteEnabled) {
          return {
            ...s,
            onAdd: () => onAddCondition(),
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              const rec = findCondition(e.id);
              if (!rec) return e;
              const actions: { label: string; onClick: () => void }[] = [
                { label: 'Edit', onClick: () => onEditCondition(e.id) },
                { label: 'Resolve', onClick: () => onResolveCondition(e.id) },
              ];
              if (visitNoteV2 && onAddProblemToNote) {
                // List A (`visitNoteV2`; Samer, 3 Nov call: the pre-encounter's add-a-note is not the encounter's "Add to note"; PROD-3414):
                // in the started encounter (the A&P's writer) the row keeps the problem's own note as "Problem note" (today's "+ Note",
                // which the encounter used to drop) beside "Add to note". The pre-visit row is unchanged (Andrew, 3 Nov: "please ignore
                // removing add to note from the pre-encounter screen"). A problem with no code: no "Add to note", as before.
                actions.push({ label: 'Problem note', onClick: () => onOpenNote(e.id) });
                const problem = toPatientProblems([rec])[0];
                if (problem) {
                  const dx = problemToCodeSearchResult(problem);
                  actions.push({ label: 'Add to note', onClick: () => onAddProblemToNote(dx) });
                }
              } else if (!onAddProblemToNote) {
                actions.push({ label: '+ Note', onClick: () => onOpenNote(e.id) });
              } else {
                // The started long page: the problem joins the Assessment, by the SAME mapping the A/P's
                // "Patient's active problems" group uses. A problem with no code cannot be added: no action.
                const problem = toPatientProblems([rec])[0];
                if (problem) {
                  const dx = problemToCodeSearchResult(problem);
                  actions.push({ label: 'Add to note', onClick: () => onAddProblemToNote(dx) });
                }
              }
              return { ...e, actions };
            }),
          };
        }
        // Resolved Problems — Edit · Unresolve · Remove.
        //
        // CORRECTION: this block used to read "NOT in the BRD taxonomy → Flutter fallback (uniform
        // Edit + Delete)". That characterisation was wrong about Flutter, not just incomplete.
        // Flutter offers Unresolve on BOTH surfaces that list resolved problems — the chart tab
        // (`problems_tab.dart:325-332`) and this rail (`clinical_info_widget.dart:388-406`, action id
        // `AppConstants.actionUnResolveProblem`). Omitting it meant a clinician could resolve a
        // problem from this rail and then have no way to reverse it without leaving the encounter.
        // Word-labeled per the design (glyphs are Allergies-only).
        if (s.id === 'resolved-problems' && clinicalWriteEnabled) {
          return {
            ...s,
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              if (!findCondition(e.id)) return e;
              return {
                ...e,
                actions: [
                  { label: 'Edit', onClick: () => onEditCondition(e.id) },
                  { label: 'Unresolve', onClick: () => onUnresolveCondition(e.id) },
                  { label: 'Remove', onClick: () => onDeleteCondition(e.id), tone: 'danger' as const },
                ],
              };
            }),
          };
        }
        // Procedures (PROD-1266). Edit/delete need the FULL chart record AND its original asserter
        // (buildProcedureRequest blocks otherwise) — suppress the affordance until that record is
        // loaded + has an asserter, so a clinician never clicks a control that would silently no-op.
        if (s.id === 'procedures' && clinicalWriteEnabled) {
          return {
            ...s,
            onAdd: () => onAddProcedure(),
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              if (!findProcedure(e.id)?.asserter) return e;
              // BRD taxonomy (Past Medical/Surgical History): Edit · Remove (danger).
              return {
                ...e,
                actions: [
                  { label: 'Edit', onClick: () => onEditProcedure(e.id) },
                  { label: 'Remove', onClick: () => onDeleteProcedure(e.id), tone: 'danger' as const },
                ],
              };
            }),
          };
        }
        // Immunizations / Vaccines (to-ips-sections id = 'vaccinations'). Edit/delete suppressed
        // until the full chart record is loaded AND carries its original performer (the update body
        // preserves it — buildImmunizationRequest blocks otherwise; never show a no-op affordance).
        if (s.id === 'vaccinations' && clinicalWriteEnabled) {
          return {
            ...s,
            onAdd: () => onAddImmunization(),
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              const rec = findImmunization(e.id);
              if (!rec?.performer) return e;
              // Flutter-fallback set (Edit + Delete), word-labeled per the design.
              return {
                ...e,
                actions: [
                  { label: 'Edit', onClick: () => onEditImmunization(e.id) },
                  { label: 'Remove', onClick: () => onDeleteImmunization(e.id), tone: 'danger' as const },
                ],
              };
            }),
          };
        }
        // Family History — add (relationship + ≥1 diagnosis row) + edit + soft-delete. Edit/delete
        // suppressed until the full chart record (per-row recorder/dates) is loaded by identifier.
        if (s.id === 'family-history' && clinicalWriteEnabled) {
          return {
            ...s,
            onAdd: () => setFamilyDialog({ open: true, editing: null }),
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              if (!findFamilyHistory(e.id)) return e;
              // BRD taxonomy (Family History): Edit · Remove (danger).
              return {
                ...e,
                actions: [
                  { label: 'Edit', onClick: () => onEditFamilyHistory(e.id) },
                  { label: 'Remove', onClick: () => onDeleteFamilyHistory(e.id), tone: 'danger' as const },
                ],
              };
            }),
          };
        }
        // Social History (CODED — to-ips-sections id = 'social-history'). Edit/delete suppressed
        // until the full chart record is loaded AND carries its original createdBy (the update body
        // preserves it — buildSocialHistoryRequest blocks otherwise; never show a no-op affordance).
        // This is the CODED section; the read-only Occupation/Education SocialSection cards are a
        // separate display rendered outside the IPS and are not made interactive here.
        if (s.id === 'social-history' && clinicalWriteEnabled) {
          return {
            ...s,
            onAdd: () => onAddSocialHistory(),
            entries: s.entries.map((e) => {
              if (isMoreTrailer(s.id, e)) return e;
              const rec = findSocialHistory(e.id);
              if (!rec?.createdBy) return e;
              // BRD (explicit): Social History = Edit ONLY — "single action, still hover-revealed".
              // NOTE this drops the only delete affordance for social-history rows (the edit dialog
              // has none) — done per the spec's explicit single-action line, flagged on the ticket;
              // the builder's 'delete' action remains for a cheap reinstate if Samer wants it back.
              return { ...e, actions: [{ label: 'Edit', onClick: () => onEditSocialHistory(e.id) }] };
            }),
          };
        }
        return s;
      })
    : ipsSections;

  // The four clinical summary lists for the DS PatientHeader card cluster, derived from the SAME
  // GetPatientSummary-driven IPS sections (display strings only). Section absent from ipsSections
  // → undefined (card omitted: not loaded); present-but-empty → [] (the card's record-describing
  // empty state, e.g. "No allergies recorded" — never an NKA claim). The synthetic "+N more" trailer never leaks into a card list.
  const headerCardList = (sectionId: string): string[] | undefined => {
    const section = ipsSections.find((s) => s.id === sectionId);
    if (!section) return undefined;
    return section.entries.filter((e) => !isMoreTrailer(sectionId, e)).map((e) => e.primary);
  };

  // Patient card — the DS PatientHeader BLOCK (DS 1.50, PROD-1588 round 3): the Figma-recovered
  // identity card (+ the four colour-accented clinical info cards, BANNER ONLY — see below).
  // Contacts pass straight through (ContactVM ≡ PatientHeaderContact). The status-control slot
  // above is KEPT exactly as-is (the interactive/read-only SessionStatusControl owns appointment
  // status — the PatientHeader `status` prop is intentionally not used). importance/VIP: rendered
  // by the DS block since 1.50.1 (top-right identity cluster).
  //
  // LAYOUT-AWARE card lists (PROD-1588 round 4, Samer touchpoint-3 reject #1): on the
  // PRE-ENCOUNTER rail the frame (E2 1736:156432) shows the COMPACT identity card + the IPS
  // segment list — NOT the 4-card cluster (which duplicated the very sections listed underneath:
  // "flags/allergies/problems cards on top THEN repeated underneath — very strange"). The DS
  // block's contract already carries the compact mode: a clinical info card renders ONLY when its
  // list is PROVIDED (`undefined` = omitted), so the rail passes no lists → identity card only.
  // The 4-card cluster belongs ONLY to the IN-ENCOUNTER banner (shell frame 1736:153260).
  /*
   * IDENTITY BLOCK — two consumers, two DS components.
   *
   * RAIL (pre-encounter): the DS `PreEncounterSummaryRail`, whose person card is `PatientHeader
   * variant="person-card"` — identity alone, no cluster.
   *
   * BANNER (in-encounter): the DS `EncounterSummaryBand` (graduated from this app, 3A DS-C), in
   * the in-encounter band — not `PatientHeader`, which is closed over a four-kind card union with
   * `string[]` lists. The band carries the live status control in the identity row, a FIFTH
   * section (Resolved Problems), one divided cluster card with an explicit half per section,
   * draggable entries, and a full-width identity card with the clinical cards below it expanded.
   */
  const importanceVM =
    importance && (importance.code || importance.display)
      ? { code: (importance.code || importance.display) as string, display: importance.display ?? undefined }
      : undefined;

  const statusControl = appointmentStatus ? <RailStatusControl appointmentStatus={appointmentStatus} appointmentId={appointmentId} /> : null;

  // PRE-ENCOUNTER identity is the DS `PreEncounterSummaryRail`'s own `PatientHeader
  // variant="person-card"` (it builds the identity run itself), so the rail passes the view
  // models unmodified — see the return at the bottom of this component.

  // ── M1 — PromotedProblemsCard (four-moments wave, spec §10.1; Samer-approved frames f1/f2) ──
  // The in-encounter Active Problems header card, promoted to an actionable presence: the body
  // rows are the APPROVED ClinicalTermRow with the surface-2 action set (+ Note · Resolve · More
  // — the SAME rail handlers, reused not duplicated); the footer "Full summary · all N" opens the
  // full IPS disclosure the banner already owns. Top 4 rows (frame anatomy); actions suppressed
  // until the full chart record is loaded (same guard as the rail rows). Banner-only.
  const activeProblemsSection = ipsSections.find((s) => s.id === 'active-problems');
  const promotedProblemEntries = (activeProblemsSection?.entries ?? []).filter(
    (e) => !isMoreTrailer('active-problems', e),
  );
  /*
   * M1's `PromotedProblemsCard` is NOT mounted any more.
   *
   * COLLAPSED, Flutter shows Active Problems as an inline summary card like its siblings
   * (`ClinicalInfoViewType.view`) — the band card above does exactly that. EXPANDED, `ipsBlock`
   * renders the same problems as per-entry rows carrying the SAME + Note / Resolve / More
   * handlers, which is Flutter's `.editable`. Mounting the promoted card too duplicated the
   * section, and at 209px (four rows + header + footer) it was the single biggest reason the
   * collapsed band could not reach Flutter's 180. Recover it from git if the band ever needs an
   * actionable card at rest.
   */

  /* The collapsed cluster's five sections. Accents are the DS role map `PatientHeader` itself
   * ships, kept identical so the band reskins with the DS rather than drifting from it. Resolved
   * Problems is the one section the DS block has no slot for (its card union is
   * flags|allergies|problems|medications) — it takes `--color-status-success` per the design.
   *
   * COLUMN IS LOAD-BEARING (design EncounterScreen.dc.html :84 / :100): the cluster is ONE card
   * split by a single rule — LEFT half = Patient Flags over Active Problems, RIGHT half =
   * Allergies over Medications over Resolved Problems. The previous four-card `repeat(2, 1fr)`
   * grid had no such concept and ordered row-major.
   */
  const bandCards: EncounterSummaryCard[] = [
    {
      id: 'flags',
      title: 'Patient Flags',
      accent: 'status-error',
      items: headerCardList('flags'),
      emptyText: emptyCopy('flags', 'No flags recorded'),
      column: 1,
    },
    {
      id: 'allergies',
      title: 'Allergies',
      accent: 'red-600',
      items: headerCardList('allergies'),
      // Describes the RECORD, never the patient: an empty list is not an NKA assertion (dev, 2026-09-30).
      emptyText: emptyCopy('allergies', 'No allergies recorded'),
      column: 2,
    },
    {
      id: 'medications',
      title: 'Medications',
      accent: 'status-warning',
      items: headerCardList('medications'),
      emptyText: emptyCopy('medications', 'No medications recorded'),
      dragKind: 'medication',
      column: 2,
    },
    {
      id: 'problems',
      title: 'Active Problems',
      accent: 'brand-primary',
      column: 1,
      // COLLAPSED = an inline summary card like its three siblings, which is what Flutter shows
      // at rest (`ClinicalInfoViewType.view`). The ACTIONABLE `PromotedProblemsCard` is 209px —
      // four rows, a header and a footer — and putting it in the cluster made the band 420px, so
      // the collapsed state cost 240px to carry actions Flutter does not show until you expand.
      // The actions are not lost: expanding swaps the whole cluster for the per-entry IPS, where
      // every row has + Note / Resolve / More. That IS Flutter's view -> editable switch.
      items: headerCardList('active-problems'),
      count: promotedProblemEntries.length,
      emptyText: emptyCopy('active-problems', 'No active problems recorded'),
      // Flutter marks problems draggable so a clinician can pull one straight onto the plan.
      dragKind: 'diagnosis',
    },
    {
      // The FIFTH section the design carries (:115-121) and the DS `PatientHeader` card union has
      // no slot for. Flutter marks resolved problems draggable too (`clinical_info_widget.dart`
      // `canDrag`), so a clinician can pull a past diagnosis onto the plan.
      id: 'resolved-problems',
      title: 'Resolved Problems',
      accent: 'status-success',
      items: headerCardList('resolved-problems'),
      emptyText: emptyCopy('resolved-problems', 'No resolved problems'),
      dragKind: 'diagnosis',
      column: 2,
    },
  ];

  /* Contacts are DATA to the band now, not a hand-built slot. The previous block bottom-pinned
     them under a divider (`marginTop: auto` + `borderTop`), which is not in Flutter's
     `emergencyContactWidget` — the source puts a plain 2-column grid straight under the
     demographics, at 32px column gap, capped at two entries. `ContactVM` already carries the
     `isProxy` flag the band renders as Flutter's green Proxy badge. */



  // Occupation / Education cards (PROD-1272) — kept OUT of headerBlock so the rail can order
  // them AFTER the IPS segment cluster (see the rail layout below, PROD-1588 wave S2).
  // Occupation / Education. PRE-ENCOUNTER renders them as the design's section cards — identical
  // anatomy to their nine siblings, because PreEncounter.dc.html opens its section list with them
  // and draws no distinction. They stay SINGLE-RECORD though (one occupation, one education), so
  // the affordance is the shipped add/edit dialog rather than a per-row action; it rides
  // `onAdd`, which is exactly the slot the card already has. The BANNER keeps SocialSection.
  const socialAsSections: IpsSection[] = [
    {
      id: 'occupation',
      title: 'Occupation',
      entries: (occupation ?? []).map((r, n) => ({
        id: `occ-${n}`,
        primary: r.label,
        secondary: r.sublabel,
      })),
      onAdd: clinicalWriteEnabled ? () => setOccupationDialogOpen(true) : undefined,
    },
    {
      id: 'education',
      title: 'Education',
      entries: (education ?? []).map((r, n) => ({
        id: `edu-${n}`,
        primary: r.label,
        secondary: r.sublabel,
      })),
      onAdd: clinicalWriteEnabled ? () => setEducationDialogOpen(true) : undefined,
    },
  ];
  const socialBlock = banner ? (
    clinicalWriteEnabled ? (
      <>
        <SocialSection
          title="Occupation"
          rows={occupation ?? []}
          onAddEdit={() => setOccupationDialogOpen(true)}
          busy={saveOccupation.isPending}
        />
        <SocialSection
          title="Education"
          rows={education ?? []}
          onAddEdit={() => setEducationDialogOpen(true)}
          busy={saveEducation.isPending}
        />
      </>
    ) : (
      <>
        {occupation && occupation.length > 0 && <SocialSection title="Occupation" rows={occupation} />}
        {education && education.length > 0 && <SocialSection title="Education" rows={education} />}
      </>
    )
  ) : null;
  // PRE-ENCOUNTER: the DS rail's leading `socialSections`. They render outside `sections`, so they
  // need the anchored surface wired here as well - otherwise their `+` was the last one in the
  // column still raising a centred modal. Single-record, so the renderer passes `existing` rather
  // than `editing={null}`: a populated card still edits its record.
  const socialCards = socialAsSections.map((sec) =>
    railSectionCard({
      section: sec,
      emptyLabel: '',
      // One record each, so the `+` opens ADD when empty and EDIT when populated.
      addLabel: `${sec.entries.length > 0 ? 'Edit' : 'Add'} ${sec.id}`,
      addPopover: railAddPopover(sec.id),
      onAddOpenChange: railAddOpenChange(sec.id),
      designV2,
    }),
  );

  // -- The pre-encounter rail's ADD surface, per section ------------------------------------
  //
  // Ahmed, 2026-09-07: every `+` in the first column opens a surface ANCHORED to the `+` that
  // was pressed, as the published provider app does - not a centred modal that dims the rail.
  // `SummarySectionCard` turns the `+` into an `AnchoredPopoverTrigger` when `addPopover` is
  // supplied and wraps what we return in `AddSurfaceInPopover`, so each dialog below emits its
  // popover body instead of its `Modal` WITHOUT any change to its internal form layout
  // (`add-surface.tsx`). The `dialogs` block further down is deliberately untouched: it still
  // serves the in-encounter BANNER and every row-level Edit / Stop / + Note flow, none of which
  // has an anchor to hang off.
  //
  // ADD, never edit: each host `onSubmitX` decides add-vs-edit by reading `xDialog.editing`, and
  // the `+` no longer sets it - so `editing={null}` here is the same null the state already
  // holds. Occupation/Education are the deliberate exception (single-record: their `+` is Add
  // when empty and Edit when populated), and they pass `existing` exactly as the block does.
  //
  // Each renderer's props are copied verbatim from that dialog's instance in `dialogs`, with two
  // additions: `close()` after a successful submit, and `close()` alongside the host's own
  // close, so the surface comes down with the state it was showing.
  function railAddPopover(sectionId: string): RailSurfaceRender | undefined {
    switch (sectionId) {
      case 'flags':
        // A SELECT-then-COMMIT form since the encounter owner ruling of 1 Nov 2026 ("Add flag"
        // commit, no commit on a row tap), so it routes through `FlagAddDialog` like every other
        // form-shaped rail dialog; `AddSurface` draws it anchored here and centred in the banner.
        return flagsWritable
          ? ({ close }) => (
              <FlagAddDialog
                open
                groups={offeredFlagGroups}
                loading={categories.isLoading}
                saving={saveFlags.isPending}
                onPick={(c) => {
                  onAddFlag(c);
                  close();
                }}
                onClose={close}
              />
            )
          : undefined;
      case 'medications':
        return medsInteractive
          ? ({ close }) => (
              <MedicationAddDialog
                open
                searchValue={medQuery}
                onSearchChange={setMedQuery}
                results={medSearch.data ?? []}
                saving={saveMedication.isPending}
                onSave={(v) => onSaveMedication(v)?.then(close)}
                interactions={drugInteractions.data}
                interactionsLoading={drugInteractions.isLoading}
                interactionsError={drugInteractions.isError}
                onInteractionsRetry={() => void drugInteractions.refetch()}
                onMedicationSelect={setMedForDi}
                onClose={() => {
                  setMedDialogOpen(false);
                  setMedQuery('');
                  close();
                }}
              />
            )
          : undefined;
      case 'allergies':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsAllergyDialog
                open
                editing={null}
                query={allergyQuery}
                onQueryChange={setAllergyQuery}
                results={allergyResults}
                searching={allergySearching}
                resolveAllergen={resolveAllergen}
                onKindChange={setAllergyKind}
                criticalityOptions={severities.data ?? []}
                reactionOptions={allergenReactions.data ?? []}
                reactionsLoading={allergenReactions.isLoading}
                criticalityLoading={severities.isLoading}
                saving={saveAllergy.isPending}
                onSubmit={(v) => {
                  onSubmitAllergy(v);
                  close();
                }}
                onClose={() => {
                  closeAllergyDialog();
                  close();
                }}
              />
            )
          : undefined;
      case 'active-problems':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsConditionDialog
                open
                editing={null}
                query={conditionQuery}
                onQueryChange={setConditionQuery}
                results={conditionResults}
                searching={dxSearch.isFetching}
                resolveCode={resolveCode}
                saving={saveCondition.isPending}
                onSubmit={(v) => {
                  onSubmitCondition(v);
                  close();
                }}
                onClose={() => {
                  closeConditionDialog();
                  close();
                }}
              />
            )
          : undefined;
      case 'procedures':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsProcedureDialog
                open
                editing={null}
                query={procedureQuery}
                onQueryChange={setProcedureQuery}
                results={procedureResults}
                searching={procedureCodeSearch.isFetching}
                resolveProcedure={resolveProcedure}
                saving={saveProcedure.isPending}
                onSubmit={(v) => {
                  onSubmitProcedure(v);
                  close();
                }}
                onClose={() => {
                  closeProcedureDialog();
                  close();
                }}
              />
            )
          : undefined;
      case 'vaccinations':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsImmunizationDialog
                open
                editing={null}
                query={vaxQuery}
                onQueryChange={setVaxQuery}
                results={immunizationResults}
                searching={vaccineCodeSearch.isFetching}
                resolveVaccine={resolveVaccine}
                saving={saveImmunization.isPending}
                onSubmit={(v) => {
                  onSubmitImmunization(v);
                  close();
                }}
                onClose={() => {
                  closeImmunizationDialog();
                  close();
                }}
              />
            )
          : undefined;
      case 'family-history':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsFamilyHistoryDialog
                open
                editing={null}
                relationshipQuery={relationshipQuery}
                onRelationshipQueryChange={setRelationshipQuery}
                relationshipResults={relationshipResults}
                relationshipSearching={relationshipSearch.isFetching}
                resolveRelationship={resolveRelationship}
                diagnosisQuery={familyDxQuery}
                onDiagnosisQueryChange={setFamilyDxQuery}
                diagnosisResults={familyDxResults}
                diagnosisSearching={familyDxSearch.isFetching}
                resolveDiagnosis={resolveFamilyDx}
                saving={saveFamilyHistory.isPending}
                onSubmit={(v) => {
                  onSubmitFamilyHistory(v);
                  close();
                }}
                onClose={() => {
                  closeFamilyDialog();
                  close();
                }}
              />
            )
          : undefined;
      case 'social-history':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsSocialHistoryDialog
                open
                editing={null}
                query={socialQuery}
                onQueryChange={setSocialQuery}
                results={socialTypeResults}
                searching={socialTypeSearch.isFetching}
                resolveType={resolveSocialType}
                quantityOptions={socialQtyList.data ?? []}
                quantityLoading={socialQtyList.isLoading}
                saving={saveSocialHistory.isPending}
                onSubmit={(v) => {
                  onSubmitSocialHistory(v);
                  close();
                }}
                onClose={() => {
                  closeSocialDialog();
                  close();
                }}
              />
            )
          : undefined;
      // Single-record cards (PROD-1272): the `+` is Add when empty and EDIT when populated, so
      // `existing` is passed exactly as the `dialogs` block passes it - forcing a null here
      // would turn a populated card's edit into a blank form.
      case 'occupation':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsOccupationDialog
                open
                existing={occupationEntity ?? null}
                saving={saveOccupation.isPending}
                onSubmit={(v) => {
                  onSubmitOccupation(v);
                  close();
                }}
                onClose={() => {
                  setOccupationDialogOpen(false);
                  close();
                }}
              />
            )
          : undefined;
      case 'education':
        return clinicalWriteEnabled
          ? ({ close }) => (
              <IpsEducationDialog
                open
                existing={educationEntity ?? null}
                levelOptions={educationLevels.data ?? []}
                levelsLoading={educationLevels.isLoading}
                saving={saveEducation.isPending}
                onSubmit={(v) => {
                  onSubmitEducation(v);
                  close();
                }}
                onClose={() => {
                  setEducationDialogOpen(false);
                  close();
                }}
              />
            )
          : undefined;
      // 'resolved-problems' has no add flow at all (its `+` was never wired) - nothing to anchor.
      default:
        return undefined;
    }
  }

  /** Reports the anchored surface's open state to whichever lazy query is gated on "is this
   *  form up" - the popover owns the state itself (a host that also WROTE it would be the
   *  broken toggle the DS warns about), so this is a side-effect notification only. Sections
   *  with no open-gated query return undefined. */
  function railAddOpenChange(sectionId: string): ((open: boolean) => void) | undefined {
    switch (sectionId) {
      case 'flags':
        return setFlagPopoverOpen;
      case 'allergies':
        return setAllergyPopoverOpen;
      case 'active-problems':
        return setConditionPopoverOpen;
      case 'procedures':
        return setProcedurePopoverOpen;
      case 'vaccinations':
        return setImmunizationPopoverOpen;
      case 'family-history':
        return setFamilyPopoverOpen;
      case 'social-history':
        return setSocialPopoverOpen;
      case 'education':
        return setEducationPopoverOpen;
      // Medications' search (`useMedicationSearchTiered`) and Occupation carry no open-gated query -
      // the med typeahead is keyed on the query string alone, which is empty until typed.
      default:
        return undefined;
    }
  }

  // -- The pre-encounter rail's ROW-ACTION surfaces -----------------------------------------
  //
  // Ahmed, twice on 2026-09-07: no control in the pre-encounter left column may open a centred
  // modal. The nine section `+` controls went first (`railAddPopover` above). These are the rest,
  // and one of them is what he was still seeing: Active Problems rows carry a "+ Note" action -
  // the only left-column control whose LABEL starts with a plus - and it still raised a `Modal`.
  //
  // Each row action already has its own 12px glyph button, so it has a perfectly good anchor;
  // `SummarySectionCard` hands that button to `AnchoredPopoverTrigger` via `render` (replaced,
  // not wrapped) whenever this lookup returns a surface for it.
  //
  // WHY THE RECORD IS THREADED AND THE HOST STATE IS NOT TOUCHED. Every edit flow here decides
  // add-vs-edit by reading its own `xDialog.editing`, and the `dialogs` fragment below renders off
  // `xDialog.open`. An anchored edit that set that state would open a CENTRED modal beside its own
  // popover - the exact thing being removed. So these renderers look the full chart record up with
  // the SAME finders the row-action gate already used, pass it as `editing`, and hand it to the
  // submit handler as its explicit second argument (see `onSubmitAllergy` and siblings). The
  // `dialogs` fragment is left alone and still serves the in-encounter BANNER, which has no anchor
  // to hang off.
  //
  // A record that is NOT found returns `undefined`, which leaves the action exactly as it was: a
  // plain button firing its own `onClick`. That is also how Remove / Resolve / Unresolve stay put -
  // direct mutations with no surface at all.
  function railActionPopover(
    sectionId: string,
  ): (entryId: string, actionLabel: string) => RailSurfaceRender | undefined {
    return (entryId, actionLabel) => {
      const isEdit = actionLabel === 'Edit';
      switch (sectionId) {
        case 'allergies': {
          if (!isEdit || !clinicalWriteEnabled) return undefined;
          const rec = findAllergy(entryId);
          if (!rec) return undefined;
          return ({ close }) => (
            <IpsAllergyDialog
              open
              editing={rec}
              query={allergyQuery}
              onQueryChange={setAllergyQuery}
              results={allergyResults}
              searching={allergySearching}
              resolveAllergen={resolveAllergen}
              onKindChange={setAllergyKind}
              criticalityOptions={severities.data ?? []}
              reactionOptions={allergenReactions.data ?? []}
              reactionsLoading={allergenReactions.isLoading}
              criticalityLoading={severities.isLoading}
              saving={saveAllergy.isPending}
              onSubmit={(v) => {
                onSubmitAllergy(v, rec);
                close();
              }}
              onClose={() => {
                closeAllergyDialog();
                close();
              }}
            />
          );
        }
        // Both problem sections edit through the ONE condition dialog and the ONE finder, exactly
        // as their row `onClick`s did (`onEditCondition` looks across active + resolved).
        case 'active-problems':
        case 'resolved-problems': {
          if (!clinicalWriteEnabled) return undefined;
          const rec = findCondition(entryId);
          if (!rec) return undefined;
          if (isEdit) {
            return ({ close }) => (
              <IpsConditionDialog
                open
                editing={rec}
                query={conditionQuery}
                onQueryChange={setConditionQuery}
                results={conditionResults}
                searching={dxSearch.isFetching}
                resolveCode={resolveCode}
                saving={saveCondition.isPending}
                onSubmit={(v) => {
                  onSubmitCondition(v, rec);
                  close();
                }}
                onClose={() => {
                  closeConditionDialog();
                  close();
                }}
              />
            );
          }
          // "+ Note" - the plus-labelled row action. Prefilled from the record so a save can
          // never silently clobber a prior note (the dialog's own single-note semantics); the
          // `dialogs` fragment keys its instance on the condition id for a fresh mount per row,
          // and here each row's action owns its own popover, so the mount is per-record already.
          // Rail v3, left-rail target §4: the pre-visit "Add a note" is the catalogue RailNote in the row (rail-v3-column.tsx), which
          // APPENDS through `rowHandlers.addPrevisitNote`; it has no surface here. "Entered in error" asks for its required reason.
          if (railV3Rows && actionLabel === 'Entered in error') {
            return ({ close }) => (
              <IpsNoteDialog
                key={entryId}
                open
                subject={rec.code?.display ?? 'Problem'}
                note=""
                required
                words={ENTERED_IN_ERROR_WORDS}
                saving={saveCondition.isPending}
                onSubmit={(reason) => {
                  onMarkEnteredInError(entryId, reason);
                  setErrorDialog(null);
                  close();
                }}
                onClose={() => {
                  setErrorDialog(null);
                  close();
                }}
              />
            );
          }
          if (actionLabel === '+ Note') {
            return ({ close }) => (
              <IpsNoteDialog
                key={entryId}
                open
                subject={rec.code?.display ?? 'Problem'}
                note={rec.note ?? ''}
                saving={saveCondition.isPending}
                onSubmit={(note) => {
                  onSubmitNote(note, rec);
                  close();
                }}
                onClose={() => {
                  setNoteDialog({ open: false, conditionId: null });
                  close();
                }}
              />
            );
          }
          return undefined;
        }
        case 'procedures': {
          if (!isEdit || !clinicalWriteEnabled) return undefined;
          const rec = findProcedure(entryId);
          if (!rec) return undefined;
          return ({ close }) => (
            <IpsProcedureDialog
              open
              editing={rec}
              query={procedureQuery}
              onQueryChange={setProcedureQuery}
              results={procedureResults}
              searching={procedureCodeSearch.isFetching}
              resolveProcedure={resolveProcedure}
              saving={saveProcedure.isPending}
              onSubmit={(v) => {
                onSubmitProcedure(v, rec);
                close();
              }}
              onClose={() => {
                closeProcedureDialog();
                close();
              }}
            />
          );
        }
        case 'vaccinations': {
          if (!isEdit || !clinicalWriteEnabled) return undefined;
          const rec = findImmunization(entryId);
          if (!rec) return undefined;
          return ({ close }) => (
            <IpsImmunizationDialog
              open
              editing={rec}
              query={vaxQuery}
              onQueryChange={setVaxQuery}
              results={immunizationResults}
              searching={vaccineCodeSearch.isFetching}
              resolveVaccine={resolveVaccine}
              saving={saveImmunization.isPending}
              onSubmit={(v) => {
                onSubmitImmunization(v, rec);
                close();
              }}
              onClose={() => {
                closeImmunizationDialog();
                close();
              }}
            />
          );
        }
        case 'family-history': {
          if (!isEdit || !clinicalWriteEnabled) return undefined;
          const rec = findFamilyHistory(entryId);
          if (!rec) return undefined;
          return ({ close }) => (
            <IpsFamilyHistoryDialog
              open
              editing={rec}
              relationshipQuery={relationshipQuery}
              onRelationshipQueryChange={setRelationshipQuery}
              relationshipResults={relationshipResults}
              relationshipSearching={relationshipSearch.isFetching}
              resolveRelationship={resolveRelationship}
              diagnosisQuery={familyDxQuery}
              onDiagnosisQueryChange={setFamilyDxQuery}
              diagnosisResults={familyDxResults}
              diagnosisSearching={familyDxSearch.isFetching}
              resolveDiagnosis={resolveFamilyDx}
              saving={saveFamilyHistory.isPending}
              onSubmit={(v) => {
                onSubmitFamilyHistory(v, rec);
                close();
              }}
              onClose={() => {
                closeFamilyDialog();
                close();
              }}
            />
          );
        }
        case 'social-history': {
          if (!isEdit || !clinicalWriteEnabled) return undefined;
          const rec = findSocialHistory(entryId);
          if (!rec) return undefined;
          return ({ close }) => (
            <IpsSocialHistoryDialog
              open
              editing={rec}
              query={socialQuery}
              onQueryChange={setSocialQuery}
              results={socialTypeResults}
              searching={socialTypeSearch.isFetching}
              resolveType={resolveSocialType}
              quantityOptions={socialQtyList.data ?? []}
              quantityLoading={socialQtyList.isLoading}
              saving={saveSocialHistory.isPending}
              onSubmit={(v) => {
                onSubmitSocialHistory(v, rec);
                close();
              }}
              onClose={() => {
                closeSocialDialog();
                close();
              }}
            />
          );
        }
        case 'medications': {
          if (!medsInteractive) return undefined;
          if (isEdit) {
            // The prefill is ASYNC (the summary row is lossy - `getMedicationDetails` fetches the
            // full record), kicked off by `railActionOpenChange` below. Until it lands, show the
            // rail's own loading body: mounting `MedicationAddDialog` first would be worse than a
            // wait, because it seeds its fields from `editing` ON MOUNT and would never pick the
            // prefill up. `key` pins one mount per record for the same reason the `dialogs`
            // fragment keys its edit instance.
            return ({ close }) => {
              const prefill = medEditDialog.identifier === entryId ? medEditDialog.prefill : null;
              // The fetch failed - come back down rather than sit on a dead "Loading...".
              if (medEditFailed === entryId) return <DismissOnMount close={close} />;
              if (!prefill) return <RailAddPopoverBody title="Edit medication" size="sm" variant="detail" loading />;
              return (
                <MedicationAddDialog
                  key={entryId}
                  open
                  searchValue=""
                  onSearchChange={() => {}}
                  results={[]}
                  editing={prefill}
                  saving={saveMedication.isPending}
                  onSave={(v) => {
                    onSubmitMedEdit(v, entryId);
                    close();
                  }}
                  interactions={drugInteractions.data}
                  interactionsLoading={drugInteractions.isLoading}
                  interactionsError={drugInteractions.isError}
                  onInteractionsRetry={() => void drugInteractions.refetch()}
                  onMedicationSelect={setMedForDi}
                  onClose={() => {
                    setMedEditDialog({ open: false, identifier: null, prefill: null });
                    close();
                  }}
                />
              );
            };
          }
          // Rail v3 (left-rail target §3): Stop is the A&P path's discontinue (the page's `medicationToPlan(id, 'stop')`), so the
          // row's own surface is not used: the action's handler runs.
          if (actionLabel === 'Stop' && canStopMedication && !railV3Rows) {
            // The whole confirm is row data (identifier + display) - it needs no host state at
            // all, so the anchored Stop never touches `medStopDialog`.
            const display = findIpsEntry('medications', entryId)?.primary ?? '';
            return ({ close }) => (
              <IpsMedStopDialog
                key={entryId}
                open
                medication={display}
                saving={saveMedication.isPending}
                onConfirm={(v) => {
                  onConfirmStopMedication(v, entryId);
                  close();
                }}
                onClose={close}
              />
            );
          }
          return undefined;
        }
        // 'flags' rows carry Remove only (a direct mutation, no surface).
        default:
          return undefined;
      }
    };
  }

  /** The row-action twin of `railAddOpenChange`: side effects that have to run while one row's
   *  surface is up. Two kinds - the lazy code-search/master queries each edit FORM needs (the
   *  same `xPopoverOpen` gates the `+` uses, because an edit form carries the same typeaheads),
   *  and the medication Edit record fetch, which is the one surface whose content is not
   *  available synchronously. Never used to drive the popover's own `open`. */
  function railActionOpenChange(
    sectionId: string,
  ): (entryId: string, actionLabel: string) => ((open: boolean) => void) | undefined {
    return (entryId, actionLabel) => {
      if (actionLabel !== 'Edit') return undefined;
      switch (sectionId) {
        case 'allergies':
          return setAllergyPopoverOpen;
        case 'active-problems':
        case 'resolved-problems':
          return setConditionPopoverOpen;
        case 'procedures':
          return setProcedurePopoverOpen;
        case 'vaccinations':
          return setImmunizationPopoverOpen;
        case 'family-history':
          return setFamilyPopoverOpen;
        case 'social-history':
          return setSocialPopoverOpen;
        case 'medications':
          // `anchored` keeps `medEditDialog.open` FALSE, so the fetched prefill parks in state for
          // the popover while the `dialogs` fragment (gated on `.open`) stays shut. Closing drops
          // it, so re-opening on another row cannot show the previous row's drug.
          return (open) => {
            setMedEditFailed(null);
            if (open) void onEditMedication(entryId, true);
            else setMedEditDialog({ open: false, identifier: null, prefill: null });
          };
        default:
          return undefined;
      }
    };
  }

  /** Chart column (frame v2) only: the row facts the summary projection does not carry, read off
   *  the FULL condition record once it has loaded — the onset year beside the name and, on a
   *  resolved row, the month it resolved. Absent record or absent date → nothing is shown; a date
   *  is never invented. Other sections carry no onset in the record the rail reads. */
  function chartRowExtras(sectionId: string): ((entryId: string) => ChartRowExtras | undefined) | undefined {
    if (sectionId === 'procedures') {
      return (entryId) => {
        const year = isoYear(findProcedure(entryId)?.onsetDateTime);
        return year === undefined ? undefined : { date: String(year) };
      };
    }
    if (sectionId === 'vaccinations') {
      return (entryId) => {
        const date = isoDayMonthYear(findImmunization(entryId)?.administratedDate);
        return date === undefined ? undefined : { date };
      };
    }
    if (sectionId !== 'active-problems' && sectionId !== 'resolved-problems') return undefined;
    return (entryId) => {
      const rec = findCondition(entryId);
      if (!rec) return undefined;
      return { onsetYear: isoYear(rec.onsetDateTime), resolvedOn: isoMonthYear(rec.endDate) };
    };
  }

  // IN-ENCOUNTER banner (expanded): the DS IPS masonry in the banner card grammar (§29, 3A DS-E1)
  // with its first-paint entry motion.
  // R6 (PROD-2804): a FAILED read is said as such, with a retry — never drawn as empty sections.
  const summaryReadError = (
    <div role="alert" className="flex flex-col items-start gap-2 p-3 font-sans">
      <p className="m-0 text-size-sm font-semibold text-text-primary">Patient summary couldn't be loaded</p>
      <p className="m-0 text-size-xs text-text-secondary">
        Allergies, problems and medications are not shown. This is not an empty record.
      </p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
  // No spinner (2026-10-12): the slot stays empty while the read is pending — never the sections' empty copy — and the
  // loaded summary arrives through its own entry motion. (The pre-encounter rail hands this same loader to the DS
  // PreEncounterSummaryRail as `loadingIndicator`, and the started chart column as `loader`.)
  // A skeleton in the LOADED panel's shape while the summary loads, so a deep link never shows an empty panel (Antonin, 19 Oct):
  // one block per section the rail will draw (three when none is known yet), each a label-role title (16 tall) over two body
  // rows (20 tall), on the loaded section card's inset, padding, gaps and hairline (showcase-bridge.css, `summary-skeleton-*`
  // under the encounter left panel). No avatar: the page header carries the patient's photo. The words stay for assistive technology.
  const skeletonKeys = sections.length > 0 ? sections.map((sec) => sec.id) : ['a', 'b', 'c'];
  const summaryLoader = (
    <div role="status" aria-busy="true" data-slot="summary-skeleton">
      <span className="sr-only">Loading patient summary…</span>
      {skeletonKeys.map((key) => (
        <div key={key} aria-hidden="true" data-slot="summary-skeleton-section">
          <Skeleton className="h-4 w-24" />
          <div data-slot="summary-skeleton-rows">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );

  // The expanded IPS has no per-section empty copy of its own (the DS writes "No {title} on file."),
  // so a section whose field was not delivered is lifted OUT of it and named in a notice above.
  const unavailableInIps = sections.filter((sec) => isUnavailable(sec.id));
  const ipsBlock = loading ? (
    summaryLoader
  ) : loadError ? (
    summaryReadError
  ) : (
    <>
    {unavailableInIps.length > 0 && (
      <p role="status" className="m-0 text-size-sm text-text-secondary">
        {unavailableInIps.map((sec) => RAIL_UNAVAILABLE_LABEL[sec.id] ?? `${sec.title} unavailable`).join(' · ')}
      </p>
    )}
    <InternationalPatientSummary
      hideHeader
      aria-label="Patient summary"
      sections={sections.filter((sec) => !isUnavailable(sec.id))}
      columns={2}
      sectionStyle="banner"
      entryMotion
    />
    </>
  );

  // PRE-ENCOUNTER: one DS `SummarySectionCard` per section. The DATA is the same `sections` array,
  // so every `onAdd` and per-entry `actions` handler the dialogs rely on is still wired.
  //   · EVERY `+` opens an ANCHORED surface, not a centred modal (Ahmed, 2026-09-07):
  //     `railAddPopover` returns that section's own add dialog rendered inside the popover;
  //     `section.onAdd` stays wired for the in-encounter banner, which has no anchor. A section
  //     whose write gate is closed gets `undefined` and keeps `onAdd`.
  //   · ...and so does every ROW action that opens a dialog: Edit on all seven writable sections,
  //     Medications' Stop, and Active Problems' "+ Note". Remove / Resolve / Unresolve are direct
  //     mutations with nothing to anchor.
  //   · every list passes an explicit empty label (`foldedEmpty`): '' for a list read empty, so the card folds to its
  //     title line (Antonin, 1 Nov) and the DS fallback `No ${title}` never renders a bare "No allergies" (PROD-2734);
  //     the "unavailable" copy for a list that was not read.
  const sectionCards = sections.map((sec) =>
    railSectionCard({
      section: sec,
      emptyLabel: foldedEmpty(sec.id),
      addPopover: railAddPopover(sec.id),
      onAddOpenChange: railAddOpenChange(sec.id),
      actionPopover: railActionPopover(sec.id),
      actionOpenChange: railActionOpenChange(sec.id),
      designV2,
    }),
  );

  // Add/edit/delete modals — identical in both layouts (position-independent overlays).
  const dialogs = (
    <>
      {flagsWritable && (
        <FlagAddDialog
          open={flagDialogOpen}
          groups={offeredFlagGroups}
          loading={categories.isLoading}
          saving={saveFlags.isPending}
          onPick={onAddFlag}
          onClose={() => setFlagDialogOpen(false)}
        />
      )}
      {medsInteractive && (
        <MedicationAddDialog
          open={medDialogOpen}
          searchValue={medQuery}
          onSearchChange={setMedQuery}
          results={medSearch.data ?? []}
          saving={saveMedication.isPending}
          onSave={onSaveMedication}
          interactions={drugInteractions.data}
          interactionsLoading={drugInteractions.isLoading}
          interactionsError={drugInteractions.isError}
          onInteractionsRetry={() => void drugInteractions.refetch()}
          onMedicationSelect={setMedForDi}
          onClose={() => {
            setMedDialogOpen(false);
            setMedQuery('');
          }}
        />
      )}
      {medsInteractive && medEditDialog.open && medEditDialog.prefill && (
        // key = the record id → fresh mount per row so the fields prefill THAT record.
        <MedicationAddDialog
          key={medEditDialog.identifier}
          open
          searchValue=""
          onSearchChange={() => {}}
          results={[]}
          editing={medEditDialog.prefill}
          saving={saveMedication.isPending}
          onSave={onSubmitMedEdit}
          interactions={drugInteractions.data}
          interactionsLoading={drugInteractions.isLoading}
          interactionsError={drugInteractions.isError}
          onInteractionsRetry={() => void drugInteractions.refetch()}
          onMedicationSelect={setMedForDi}
          onClose={() => setMedEditDialog({ open: false, identifier: null, prefill: null })}
        />
      )}
      {medsInteractive && canStopMedication && medStopDialog.open && (
        <IpsMedStopDialog
          key={medStopDialog.identifier ?? undefined}
          open
          medication={medStopDialog.display}
          saving={saveMedication.isPending}
          onConfirm={onConfirmStopMedication}
          onClose={() => setMedStopDialog({ open: false, identifier: null, display: '' })}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsAllergyDialog
          open={allergyDialog.open}
          editing={allergyDialog.editing}
          query={allergyQuery}
          onQueryChange={setAllergyQuery}
          results={allergyResults}
          searching={allergySearching}
          resolveAllergen={resolveAllergen}
          onKindChange={setAllergyKind}
          criticalityOptions={severities.data ?? []}
          reactionOptions={allergenReactions.data ?? []}
          reactionsLoading={allergenReactions.isLoading}
          criticalityLoading={severities.isLoading}
          saving={saveAllergy.isPending}
          onSubmit={onSubmitAllergy}
          onClose={closeAllergyDialog}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsConditionDialog
          open={conditionDialog.open}
          editing={conditionDialog.editing}
          query={conditionQuery}
          onQueryChange={setConditionQuery}
          results={conditionResults}
          searching={dxSearch.isFetching}
          resolveCode={resolveCode}
          saving={saveCondition.isPending}
          onSubmit={onSubmitCondition}
          onClose={closeConditionDialog}
        />
      )}
      {clinicalWriteEnabled && railV3Rows && noteDialog.open && noteDialog.conditionId && (
        // Rail v3 (left-rail target §4): the centred twin of the row's "Add a note" surface — APPEND, never replace.
        <IpsNoteDialog
          key={`append-${noteDialog.conditionId}`}
          open
          subject={findCondition(noteDialog.conditionId)?.code?.display ?? 'Problem'}
          note=""
          required
          words={{ title: 'Add a note', commit: 'Add note' }}
          saving={saveCondition.isPending}
          onSubmit={(text) => {
            if (noteDialog.conditionId) onAppendProblemNote(noteDialog.conditionId, text);
            setNoteDialog({ open: false, conditionId: null });
          }}
          onClose={() => setNoteDialog({ open: false, conditionId: null })}
        />
      )}
      {clinicalWriteEnabled && railV3Rows && errorDialog && (
        <IpsNoteDialog
          key={`error-${errorDialog}`}
          open
          subject={findCondition(errorDialog)?.code?.display ?? 'Problem'}
          note=""
          required
          words={ENTERED_IN_ERROR_WORDS}
          saving={saveCondition.isPending}
          onSubmit={(reason) => {
            onMarkEnteredInError(errorDialog, reason);
            setErrorDialog(null);
          }}
          onClose={() => setErrorDialog(null)}
        />
      )}
      {clinicalWriteEnabled && !railV3Rows && noteDialog.open && noteDialog.conditionId && (
        // key = the condition id → a fresh mount per row, so the TextArea prefills THAT row's note.
        <IpsNoteDialog
          key={noteDialog.conditionId}
          open
          subject={findCondition(noteDialog.conditionId)?.code?.display ?? 'Problem'}
          note={findCondition(noteDialog.conditionId)?.note ?? ''}
          saving={saveCondition.isPending}
          onSubmit={onSubmitNote}
          onClose={() => setNoteDialog({ open: false, conditionId: null })}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsProcedureDialog
          open={procedureDialog.open}
          editing={procedureDialog.editing}
          query={procedureQuery}
          onQueryChange={setProcedureQuery}
          results={procedureResults}
          searching={procedureCodeSearch.isFetching}
          resolveProcedure={resolveProcedure}
          saving={saveProcedure.isPending}
          onSubmit={onSubmitProcedure}
          onClose={closeProcedureDialog}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsImmunizationDialog
          open={immunizationDialog.open}
          editing={immunizationDialog.editing}
          query={vaxQuery}
          onQueryChange={setVaxQuery}
          results={immunizationResults}
          searching={vaccineCodeSearch.isFetching}
          resolveVaccine={resolveVaccine}
          saving={saveImmunization.isPending}
          onSubmit={onSubmitImmunization}
          onClose={closeImmunizationDialog}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsFamilyHistoryDialog
          open={familyDialog.open}
          editing={familyDialog.editing}
          relationshipQuery={relationshipQuery}
          onRelationshipQueryChange={setRelationshipQuery}
          relationshipResults={relationshipResults}
          relationshipSearching={relationshipSearch.isFetching}
          resolveRelationship={resolveRelationship}
          diagnosisQuery={familyDxQuery}
          onDiagnosisQueryChange={setFamilyDxQuery}
          diagnosisResults={familyDxResults}
          diagnosisSearching={familyDxSearch.isFetching}
          resolveDiagnosis={resolveFamilyDx}
          saving={saveFamilyHistory.isPending}
          onSubmit={onSubmitFamilyHistory}
          onClose={closeFamilyDialog}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsSocialHistoryDialog
          open={socialDialog.open}
          editing={socialDialog.editing}
          query={socialQuery}
          onQueryChange={setSocialQuery}
          results={socialTypeResults}
          searching={socialTypeSearch.isFetching}
          resolveType={resolveSocialType}
          quantityOptions={socialQtyList.data ?? []}
          quantityLoading={socialQtyList.isLoading}
          saving={saveSocialHistory.isPending}
          onSubmit={onSubmitSocialHistory}
          onClose={closeSocialDialog}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsOccupationDialog
          open={occupationDialogOpen}
          existing={occupationEntity ?? null}
          saving={saveOccupation.isPending}
          onSubmit={onSubmitOccupation}
          onClose={() => setOccupationDialogOpen(false)}
        />
      )}
      {clinicalWriteEnabled && (
        <IpsEducationDialog
          open={educationDialogOpen}
          existing={educationEntity ?? null}
          levelOptions={educationLevels.data ?? []}
          levelsLoading={educationLevels.isLoading}
          saving={saveEducation.isPending}
          onSubmit={onSubmitEducation}
          onClose={() => setEducationDialogOpen(false)}
        />
      )}
    </>
  );

  // ── Banner (IN-encounter): the shell frame's summary band, now built to the EncounterScreen
  //    design doc rather than the DS `PatientHeader` container-query layout.
  //      COLLAPSED (design :67-124) — a 288px identity card beside ONE cluster card split into two
  //        halves by a single 1px rule. FIVE sections, Resolved Problems included; the DS block's
  //        card union is four kinds, which is one of the reasons the band stays app-local.
  //      EXPANDED (design :127-291) — the identity card spans the FULL width, and the interactive
  //        per-entry IPS (add/edit/resolve — Flutter `isPatientInfoViewExpanded` parity) renders
  //        BELOW it. Previously the identity card stayed pinned in the left column on expand.
  //    The full IPS stays behind the explicit disclosure; collapsed is bounded by the shell's
  //    180px region clip with the chevron on that boundary as the affordance.
  /** The in-encounter band. COLLAPSED (design :67-124) = the 288px identity card beside the split
   *  cluster card. EXPANDED (design :127-291) = the identity card goes FULL WIDTH and the per-entry
   *  IPS masonry renders below it — Flutter's `view` -> `editable` switch, and the layout change
   *  that the previous composition (identity pinned in the left 311px column) did not make. */
  const bandBlock = (
    <EncounterSummaryBand
      patient={{ name: patient.name, avatar: patient.avatar }}
      expanded={bannerExpanded}
      animateEntry
      importance={importanceVM}
      statusSlot={statusControl}
      phone={demographics?.phone}
      /* MRN is NOT pre-joined into the run any more: collapsed the design typesets it on its own
         `--font-mono` line (:77), expanded it rides the demographics run (:144). The band owns
         that split, so it needs the raw value. */
      mrn={demographics?.mrn}
      demographics={joinRun([
        demographics?.ageDisplay ?? (demographics?.age != null ? `${demographics.age}y` : undefined),
        demographics?.sex && (SEX_DISPLAY[demographics.sex] ?? demographics.sex),
        demographics?.maritalStatus,
        demographics?.dob && `DOB ${demographics.dob}`,
      ], ' · ')}
      /* Flutter joins language + interpreter with a HYPHEN, not a bullet
         (`"$language - Interpreter Required"`, patient_info_widget.dart:276). Nationality is not
         in the source line but is kept here, appended as its own run. */
      demographicsSecondary={joinRun(
        [
          joinRun([demographics?.language, demographics?.interpreterRequired && 'Interpreter Required'], ' - '),
          demographics?.nationality,
        ],
        ' · ',
      )}
      contacts={contacts}
      /* R6: while loading or failed, the collapsed band's `children` (below) carry the loader or the
         failure — and the band renders `children` IN PLACE of the cluster, so these cards are not
         drawn until the read has succeeded. */
      cards={bannerExpanded ? undefined : bandCards}
    >
      {/* ONE child expression, deliberately: the band treats ANY truthy `children` as "render this
          in the cluster's place", and a two-slot array is truthy even when both slots are empty,
          which would blank the loaded cluster. */}
      {bannerExpanded ? (
        /* `id` is load-bearing: the disclosure toggles reference it via `aria-controls`, so the
           expanded region has to keep the anchor the collapsed control points at. */
        <div id="patient-summary-full" className="flex flex-col gap-4 min-w-0">
          {ipsBlock}
          {socialBlock}
        </div>
      ) : loading ? (
        summaryLoader
      ) : loadError ? (
        summaryReadError
      ) : undefined}
    </EncounterSummaryBand>
  );

  // ── Rail v3 (left-rail target 4 Nov §2; ruling 4, 1 Nov, D-5315): the within-section order, from the full chart records
  // this rail already reads, frozen for the page session (ported from origin/claude/rail-v3-behaviour, its flag dropped).
  // Facts a record does not carry sort last (see `sortSection`): resolved problems read `abatementDate ?? endDate`, and the
  // condition record carries no `abatementDate` today, so `endDate` decides. A section freezes only once its records have
  // settled (or are not read on this host) and the summary itself has loaded. The legacy rails keep the wire order.
  const highCriticality = (c: { code?: string | null; display?: string | null } | null | undefined) =>
    [c?.code, c?.display].some((v) => (v ?? '').trim().toLowerCase() === 'high') ? 'high' : (c?.display ?? c?.code ?? null);
  const settledQuery = (q: { isLoading: boolean }) => !q.isLoading;
  const chartSortInput = (sectionId: string): ChartSectionSortInput => {
    const summaryReady = !loading;
    switch (sectionId) {
      case 'allergies':
        return {
          records: factsById(allergyRecords.data, (a: ChartAllergy) => ({
            date: a.recordedDate,
            criticality: highCriticality(a.criticality),
          })),
          settled: summaryReady && settledQuery(allergyRecords),
          fetching: !!allergyRecords.isFetching,
        };
      case 'active-problems':
        return {
          records: factsById(activeConditions.data, (c: ChartCondition) => ({ date: c.createdDate })),
          settled: summaryReady && settledQuery(activeConditions),
          fetching: !!activeConditions.isFetching,
        };
      case 'resolved-problems':
        return {
          records: factsById(resolvedConditions.data, (c: ChartCondition & { abatementDate?: string | null }) => ({
            date: c.abatementDate ?? c.endDate,
          })),
          settled: summaryReady && settledQuery(resolvedConditions),
          fetching: !!resolvedConditions.isFetching,
        };
      case 'flags':
        return {
          records: factsById(flagRecords, (f: PatientFlag) => ({ date: f.createdDate })),
          settled: summaryReady && (!interactive || Array.isArray(flagRecords)),
        };
      default:
        return { settled: summaryReady };
    }
  };
  const railV3Ordered = useStableChartOrder(patientId, railV3 && !banner, sections, chartSortInput);

  /** Rail v3 (left-rail target §4): the plain row callbacks per section, for the catalog's RailNote and hover card. */
  const railV3RowHandlers = (sectionId: string): RailRowHandlers | undefined => {
    if (!clinicalWriteEnabled && sectionId === 'active-problems') return undefined;
    const started = railV3Stage === 'started';
    const note = {
      addToNote: (rowId: string, targetId: RailNoteTargetId, typedText?: string) => addToNote(sectionId, rowId, targetId, typedText),
      targetsFor: (rowId: string) => railNoteTargets(sectionId, rowId) ?? { targets: [] },
    };
    if (sectionId === 'active-problems') {
      return started
        ? { ...note, ...(railV3Ctx.persona === 'physician' ? { enteredInError: onMarkEnteredInError } : {}) }
        : { addPrevisitNote: onAppendProblemNote };
    }
    if (sectionId === 'medications' && started && medsInteractive) return note;
    // §4b round 4: an allergy goes to HPI · ROS (· Assessment). The handler is here for the row's note; the allergy row's own
    // "Add to note" action is not in the action table yet (§7.4, with the picker).
    if (sectionId === 'allergies' && started && clinicalWriteEnabled) return note;
    return undefined;
  };

  if (banner) {
    return (
      <section
        aria-label="Patient summary"
        className="flex flex-col gap-3 font-sans"
      >
        {/* ONE component owns the whole band in both states — identity left with the status
            control in its header row, and either the 2x2 cluster or the expanded IPS masonry
            beside it. The previous composition hand-assembled this from `PatientHeader` plus
            sibling blocks, which is what produced the full-width identity card on expand and
            the empty fourth grid cell when collapsed. */}
        {bandBlock}
        {/* The rail owns this toggle ONLY when uncontrolled. Under the shell chevron
            (`expanded` supplied) it would be a second control for the same state. */}
        {!controlled && (
          <div className="flex justify-center">
            <Button
              variant="tertiary"
              size="sm"
              aria-expanded={bannerExpanded}
              aria-controls="patient-summary-full"
              onClick={() => setBannerExpanded((v) => !v)}
            >
              {bannerExpanded ? 'Hide full summary' : 'Full summary'}
            </Button>
          </div>
        )}
        {dialogs}
      </section>
    );
  }

  // STEADY PROBLEM ROWS (Samer, 1 Oct 2026: "Clicking problem items opens the correct edit dialog
  // without layout shifts"). Problem rows are drawn from the summary read (`ipsSections`), but
  // their row actions (Edit · Resolve · + Note / Edit · Unresolve · Remove) attach only once the
  // condition CHART records arrive (`findCondition`, the guard above that keeps a clinician off a
  // control that would no-op). Two reads, two arrival times: when the summary lands first, every
  // problem row paints without its action column, then gains it — the DS row reserves the
  // hover-glyph box only when the actions exist — so each term re-wraps narrower and the rail
  // below it moves. Switch ON: the section list waits behind the rail's own loader until those
  // records have settled (success OR failure — `isLoading` is the first fetch only, so a refetch
  // after a write never brings the loader back), and every problem row's first paint is its final
  // geometry. OFF: today's progressive paint, unchanged.
  const problemRecordsLoading =
    clinicalWriteEnabled && (activeConditions.isLoading || resolvedConditions.isLoading);
  const sectionsLoading = loading || (designV2 && problemRecordsLoading);

  // ── Chart column (frame v2 LEADING, switch ON only — PROD-2743) ──────────────────────────────
  // The SAME interactive `sections` and the SAME anchored add / row-action renderers the pre-visit
  // rail hands its cards, laid out on the approved design by `ChartColumnV2`. Nothing is dropped:
  // every section the rail has, plus Occupation / Education, is passed; the column orders them.
  // Frame V2 only: the old frame's banner layout keeps today's render with the switch on too.
  const railV3Here = railV3 && !banner;
  if (chartColumn || railV3Here) {
    const chartSections: ChartColumnSection[] = [
      // Rail v3: rows in the ruled, session-stable order (`railV3Ordered`); the legacy chart column keeps the wire order.
      ...(railV3Here ? railV3Ordered : sections).map((sec) => ({
        section: sec,
        emptyLabel: foldedEmpty(sec.id),
        unavailable: isUnavailable(sec.id),
        addPopover: railAddPopover(sec.id),
        onAddOpenChange: railAddOpenChange(sec.id),
        actionPopover: railActionPopover(sec.id),
        actionOpenChange: railActionOpenChange(sec.id),
        rowExtras: chartRowExtras(sec.id),
        ...(railV3Here ? { rowHandlers: railV3RowHandlers(sec.id) } : {}),
      })),
      ...socialAsSections.map((sec) => ({
        section: sec,
        emptyLabel: '',
        filled: sec.entries.length > 0,
        addLabel: `${sec.entries.length > 0 ? 'Edit' : 'Add'} ${sec.id}`,
        addPopover: railAddPopover(sec.id),
        onAddOpenChange: railAddOpenChange(sec.id),
      })),
    ];
    if (railV3Here) {
      // Both Frame V2 layouts draw the restyled rail from the SAME sections and handlers. The chart layout is the started
      // visit once `started`; the pre-encounter rail is the pre-visit stage.
      return (
        <RailV3Column
          stage={chartColumn && started ? 'started' : 'pre-visit'}
          person={{
            patient,
            demographics,
            importance: importanceVM,
            contacts,
            ...(!chartColumn
              ? identityInHeader
                ? { identityInHeader: true }
                : { statusSlot: statusControl }
              : hideStatus
                ? {}
                : statusChip
                  ? { status: appointmentStatus }
                  : { statusSlot: statusControl }),
            started: chartColumn ? started : undefined,
          }}
          sections={chartSections}
          // Device data in the left rail only once the visit has started (owner ruling, 1 Nov: the pre-visit shows it as its
          // own card under the vitals, so here it would be the same card twice; Samer, 4 Nov).
          deviceData={chartColumn && started ? <DeviceTrendsSlot patientId={patientId} variant="section" /> : undefined}
          loading={sectionsLoading}
          loadFailed={loadError && !loading}
          onRetry={onRetry}
        >
          {dialogs}
        </RailV3Column>
      );
    }
    return (
      <ChartColumnV2
        // `statusChip` (the started visit): the DS StatusChip the design draws, in the D-49 tones —
        // the DS SessionStatusControl has no `tone`, so it can only draw the solid pill. Otherwise
        // the control, as on the pre-visit card.
        person={{
          patient,
          demographics,
          importance: importanceVM,
          contacts,
          ...(hideStatus ? {} : statusChip ? { status: appointmentStatus } : { statusSlot: statusControl }),
          started,
        }}
        sections={chartSections}
        // Started visit only, as above: the pre-visit has its own card under the vitals.
        deviceData={started ? <DeviceTrendsSlot patientId={patientId} variant="section" /> : undefined}
        loading={sectionsLoading}
        loader={summaryLoader}
        loadError={loadError && !loading ? summaryReadError : undefined}
      >
        {dialogs}
      </ChartColumnV2>
    );
  }

  // ── Rail (default, PRE-encounter): the DS `PreEncounterSummaryRail` — the published Flutter
  //    column (PROD-1549, measured at 1920 / zoom 100 % / DPR 1): person card, then Occupation +
  //    Education LEADING (PreEncounter.dc.html opens its section list with them; the in-encounter
  //    banner keeps the old order), then one card per IPS section. The dialogs render last inside
  //    the aside, exactly where this file mounted them. ──
  return (
    <PreEncounterSummaryRail
      // No spinner on the pre-encounter either (PROD-3194): the DS rail draws the brand loader unless given one.
      loadingIndicator={summaryLoader}
      patient={{ name: patient.name, avatar: patient.avatar }}
      demographics={demographics}
      importance={importanceVM}
      contacts={contacts}
      statusSlot={statusControl}
      avatarPlaceholderSrc={patientUserPicUrl}
      /* R6: while the reads are in flight the social cards would read "No occupation added" — hold
         them back with the sections; on a failed summary read the sections give way to the error. */
      socialSections={loading ? [] : socialCards}
      sections={loadError && !loading ? [] : sectionCards}
      loading={sectionsLoading}
    >
      {loadError && !loading ? summaryReadError : null}
      {dialogs}
    </PreEncounterSummaryRail>
  );
}
